import { Injectable, OnDestroy } from '@angular/core';

import * as uuid from 'uuid';

import {
    BriefingBlockTypeEnum} from '../enums/briefing-block-type.enum';
import {
    briefingBlockTypeEnumToBlockTypeMap,
    briefingCuratorBlockType,
} from '../models/briefings-block-types.model';
import { BriefingModel } from '../models/briefing.model';
import { BriefingStyleStateEnum } from '../../../Common/MonitorShared/enum/briefing-style-state.enum';
import { LayoutDisplayElement } from '../../../Common/PageBuilder/models/layout-display-element.model';
import { LayoutBlock } from '../../../Common/PageBuilder/models/layout-block.model';
import { LayoutBlockType } from '../../../Common/PageBuilder/models/layout-block-type.model';
import { BriefingDeliveryMethodEnum } from '../enums/briefing-delivery-method.enum';
import { BlockDisplayTypeEnum } from '../../../Common/PageBuilder/enums/block-display-type.enum';
import { BriefingsService } from './briefings.service';
import { monitoringSeriesTypeEnumToMentionSourceApiTypesEnum } from '../../../api/mentions/enum/mention-source-api-types.enum';
import { deliverySchedulingTypesEnum } from '../../../Common/Forms/enums/delivery-scheduling-types.enum';
import { FontAlignmentEnum } from '../../../Common/Forms/enums/font-alignment.enum';
import { VerticalAlignmentEnum } from '../../../Common/Forms/enums/vertical-alignment.enum';
import { SavedSearchType } from '../../../Common/MonitorShared/enum/saved-search-type.enum';
import { BriefingChartDisplayElementModel } from '../../../Common/PageBuilder/models/briefing-chart-display-element.model';
import { DateDisplayElementModel } from '../../../Common/PageBuilder/models/date-display-element.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { BriefingTextDisplayElementModel } from '../models/briefing-text-display-element.model';
import { DividerDisplayElementModel } from '../../../Common/PageBuilder/models/divider-display-element.model';
import {
    seriesSourceTypesToSeriesTypesMap,
    mentionSourceApiTypesEnumToSeriesSourceTypesEnum,
    SeriesTypeEnum
} from '../../../Common/Reports/enums/series-type.enum';
import { reportBlockTypesMap } from '../../../Common/Reports/models/reports-block-types.model';
import { CoverageInsights } from '../../Shared/models/coverage-insights.model';
import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { SentimentModel } from '../../Shared/models/sentiment.model';
import { AnalysisDisplayElementModel } from '../models/briefing-analysis-display-element.model';
import { BriefingTitleDisplayElementModel } from '../models/briefing-title-display-element.model';
import { CuratedMentionListDisplayElementModel } from '../models/curated-mention-list-display-element.model';
import { OnlineBriefingTemplateConfigModel } from '../models/online-briefing-option.model';
import { DynamicMentionListDisplayElementModel } from '../models/dynamic-mention-list-display-element.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { ReportBlockTypeEnum, reportChartInfoMap } from '../../../Common/Reports/enums/report-block-type.enum';
import { ChartSpecificTypesEnum } from '../../../Common/Reports/enums/chart-specific-types.enum';
import { ReportDateRangeModel } from '../../../Common/Reports/models/report-date-range.model';
import { ChartMetricsEnum } from '../../../Common/Reports/enums/chart-metrics.enum';
import { ReportSeriesModel } from '../../../Common/Reports/models/report-series.model';
import { ChartDisplayElementModel } from '../../../Common/Reports/models/chart-display-element.model';
import { MapChartChoroplethInterpolationTypeEnum, MapChartColorSchemeEnum } from '../../../Common/Charts/models/map-chart.model';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { StackedChartTypesEnum } from '../../../Common/Reports/enums/stacked-chart-types.enum';
import { AiAnalysisTypes } from '../../../Common/Mentions/enums/ai-analysis-types.enum';
import { BriefingImageFrameEnum } from '../../Shared/enums/briefing-image-frame.enum';
import { BriefingImageDisplayElementModel } from '../../../Common/PageBuilder/models/briefing-image-display-element.model';
import { BriefingImageDisplayElementApiModel } from '../../../api/briefings/model/briefing-image-display-element-api.model';
import { topContactsNeoFacetRequest, topOutletsNeoFacetRequest } from '../../../Common/Reports/models/report-neo-facet-requests.model';
import { BriefingRecurrencePatternModel } from '../models/briefing-recurrence-pattern.model';
import { DeliveryRecurrenceFrequencyEnum } from '../../../Common/Forms/enums/delivery-recurrence-frequency.enum';
import { filter, Subscription } from 'rxjs';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { TimeHelper } from '../../../Common/Forms/helpers/time.helper';
import { TimeModel } from '../../../Common/Forms/models/time.model';
import { TwentyFourHourModel } from '../../../Common/Forms/models/twenty-four-hour.model';
import { MinuteModel } from '../../../Common/Forms/models/minute.model';
import { timeFormatsEnum } from '../../../Core/Users/enums/time-formats.enum';
import { CoverageMetricChartDisplaySettings } from '../../../Common/Charts/models/coverage-metric-chart-display-settings.model';
import { CoverageMetricChartDisplayElement } from '../../../Common/Reports/models/coverage-metric-chart-display-element.model';
import { ReportsService } from '../../../Common/Reports/services/reports.service';
import { PaddingModel } from '../../../Common/PageBuilder/models/padding.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';
import { ImageSizeEnum } from '../../../Monitor/Shared/enums/image-size.enum';
import { BriefingHeadingLevelEnum } from '../enums/briefing-heading-level.enum';
import { ChartTimeIntervalsEnum } from '../../../Common/Reports/enums/chart-time-intervals.enum';
import { CuratorDisplayElement } from '../../../Common/PageBuilder/models/curator-display-element.model';
import { CompletionLengthEnum } from '../../../api/ai/enums/completion-length.enum';
import { OnlineBriefingBannerStyleEnum } from '../enums/online-briefing-banner-style.enum';
import { OnlineBriefingTemplateEnum } from '../enums/online-briefing-template.enum';
import { directionsEnum } from '@Common/Misc/enums/directions.enum';
import { ImageSourceEnum } from '@Common/PageBuilder/enums/image-source.enum';
import { BriefingImagePropertiesModel } from '../../../Common/PageBuilder/models/briefing-image-properties.model';
import { MonitoringBackEndDataSourceModel } from '@Monitor/Shared/models/monitoring-back-end-data-source.model';
import { LayoutBlockDisplayRatiosEnum, layoutBlockDisplayRatiosToWidthPercentMap } from '@Common/PageBuilder/enums/layout-block-display-ratios.enum';
import { getAllMediaTypes } from '@Common/Basic/enums/media-type.enum';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';
import { CornerRadiusModel } from '@Common/PageBuilder/models/corner-radius.model';
import { TermTypesEnum } from '@Common/Reports/enums/term-types.enum';
import { BriefingSortByEnum } from '../enums/briefing-sort-by.enum';
import { LabelDisplayOptionsEnum } from '@Common/Misc/enums/label-display-options.enum';
import { MentionSearchCriteriaModel } from '@Monitor/Shared/models/mention-search-criteria.model';
import { LocalizationReplacementsModel } from '../../../Core/Localization/models/localization-replacements.model';
import moment from 'moment-timezone';
import { MonitoringBrand } from '../../Shared/models/monitoring-brand.model';
import { briefingAnnotatableBlockTypes } from '@Common/Reports/models/new-data-processing-workflow-block-types.model';
import { MonthlyDateRangeSettingsModel, MonthOptionEnum, monthOptionLocalizationKeys } from '@Monitor/Shared/models/monthly-date-range-settings.model';
import { BriefingTemplateConfig, MonthlyConfig, QuarterlyConfig } from '../models/briefing-report-template.interface';


@Injectable()
export class BriefingsTemplateFactorService implements OnDestroy {
    readonly MAX_CANVAS_BLOCK_MENTIONS: number = 100;
    readonly DEFAULT_PREVIEW_MENTIONS: number = 5;
    readonly CHART_ROW_HEIGHT: number = 125;
    // Default chart height for Coverage By Region Chart with "Map" type and "Region Colors" color scheme
    readonly MAP_CHART_WITH_REGION_COLORS_CHART_HEIGHT: number = 550;

    subscriptions: Subscription[] = [];

    timezone: string;
    timeFormat: timeFormatsEnum = timeFormatsEnum.twelveHour;
    momentDateFormat: string = 'MMMM DD, YYYY';

    constructor(
        private briefingsService: BriefingsService,
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService,
        private timeHelper: TimeHelper,
        private reportsService: ReportsService
    ) {
        // Subscribe here, not in ngOnInit: Angular does not call lifecycle hooks on services, so the
        // ngOnInit subscription never ran and this.timezone stayed undefined — which made
        // getQuarterlyDateBounds() (and scheduling) fall back to browser-local time instead of the
        // user's preference timezone.
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(
                filter(x => !!x)
            ).subscribe(preferences => {
                this.timezone = preferences.timeZone;
                this.timeFormat = preferences.timeFormat;
                this.momentDateFormat = preferences.dateFormatId
                    ? preferences.getDateFormatString(true)
                    : 'MMMM DD, YYYY';
            })
        );
    }


    public getBlankCanvas(dateFormat?: string): BriefingModel {
        const blankBriefing = new BriefingModel();
        blankBriefing.deliveryMethod = BriefingDeliveryMethodEnum.ONLINE;
        blankBriefing.blocks = [];

        const defaultGeneralStyles: any = this.briefingsService.getDefaultStyleSet(BriefingStyleStateEnum.CANVAS_1);
        this.briefingsService.setDefaultGeneralStylesWithUserPreferences(defaultGeneralStyles);
        blankBriefing.setGeneralStyles(defaultGeneralStyles);

        if (dateFormat) {
            blankBriefing.generalStyles.date.dateFormat = dateFormat;
        }
        this.briefingsService.setGeneralStyles(blankBriefing.generalStyles);

        return blankBriefing;
    }

    public getTemplate(options: OnlineBriefingTemplateConfigModel): BriefingModel {
        if (options.template === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR) {
            return this.getCompetitorTemplate(options);
        }
        if (options.template === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_QUARTERLY) {
            return this.getGenericReportTemplate(options, QuarterlyConfig);
        }
        if (options.template === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_MONTHLY) {
            return this.getGenericReportTemplate(options, MonthlyConfig);
        }
        return this.getMediaCoverageBriefingTemplate(options);
    }

    public getMediaCoverageBriefingTemplate(options: OnlineBriefingTemplateConfigModel): BriefingModel {
        const newBriefing: BriefingModel = new BriefingModel();

        if (options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring) {
            newBriefing.sendAt.sendNow = false;
            newBriefing.sendAt.recurring = new BriefingRecurrencePatternModel();
            // every day of the week means daily recurrence
            newBriefing.sendAt.recurring.frequencyValues = [0, 1, 2, 3, 4, 5, 6];
            newBriefing.sendAt.recurring.frequencyType = DeliveryRecurrenceFrequencyEnum.daily;

            newBriefing.sendAt.recurring.times = [this.timeHelper.getTimeFromDate(
                new Date(),
                this.timezone,
                this.timeFormat
            )];
            newBriefing.sendAt.timeZone = this.timezone;
        }
        newBriefing.deliveryMethod = BriefingDeliveryMethodEnum.ONLINE;

        const whiteBackground = '#ffffff';

        let generalStyles = JSON.parse(JSON.stringify(this.briefingsService.getDefaultStyleSet(BriefingStyleStateEnum.CANVAS_1)));
        this.briefingsService.setDefaultGeneralStylesWithUserPreferences(generalStyles);
        generalStyles = this.setTemplateGeneralStyles(generalStyles, options);
        newBriefing.setGeneralStyles(generalStyles);
        this.briefingsService.setGeneralStyles(generalStyles);

        newBriefing.subject = this.localizationService.transform('Briefing.onlineBriefing.template.default');

        if (options.bannerStyle === OnlineBriefingBannerStyleEnum.NO_IMAGE) {
            // heading title
            newBriefing.blocks.push(
                this.getCanvasTitle(
                    options.name,
                    whiteBackground,
                    BriefingHeadingLevelEnum.HEADING_1,
                    FontAlignmentEnum.center
                )
            );

            // date
            newBriefing.blocks.push(
                this.getCanvasDate(whiteBackground)
            );
            newBriefing.blocks.forEach(x => x.displayElement.isBoundToGlobalStyles = false);
        } else {
            // layout block
            const layoutBlock = this.getCanvasLayout(whiteBackground, BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT);
            layoutBlock.displayElement.isBoundToGlobalStyles = false;
            layoutBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
            layoutBlock.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;

            // Image
            const imageFrame = options.bannerStyle === OnlineBriefingBannerStyleEnum.IMAGE_GRID ?
                BriefingImageFrameEnum.COLLAGE : BriefingImageFrameEnum.NO_FRAME;

            const imageFrameBlock = this.getCanvasImage(
                whiteBackground,
                imageFrame,
                options.accentColor,
                options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring
            );

            imageFrameBlock.displayElement.isBoundToGlobalStyles = false;
            (imageFrameBlock.displayElement as BriefingImageDisplayElementModel).styles.padding = new PaddingModel(20);
            (imageFrameBlock.displayElement as BriefingImageDisplayElementModel).styles.cornerRadius = new CornerRadiusModel(12, false);

            layoutBlock.blocks[0].push(
                imageFrameBlock
            );

            // brand logo
            if (options.logo?.length) {
                const logoImage = this.getCanvasLogoImage(
                    whiteBackground,
                    BriefingImageFrameEnum.NO_FRAME,
                    options.accentColor,
                    false,
                    options.logo
                );
                layoutBlock.blocks[1].push(logoImage);
            }

            // brand name
            const brandTitle = this.getCanvasTitle(
                options.name,
                whiteBackground,
                BriefingHeadingLevelEnum.HEADING_1,
                FontAlignmentEnum.center
            );
            (brandTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel();
            (brandTitle.displayElement as BriefingTitleDisplayElementModel).styles.color = options.accentColor;
            layoutBlock.blocks[1].push(brandTitle);

            // template name
            const subTitle = this.getCanvasTitle(
                'Briefing.onlineBriefing.template.mediaCoverage.heading',
                whiteBackground,
                BriefingHeadingLevelEnum.HEADING_1,
                FontAlignmentEnum.center
            );

            (subTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel();
            (subTitle.displayElement as BriefingTitleDisplayElementModel).styles.fontSize = '18px';
            layoutBlock.blocks[1].push(subTitle);

            // date
            layoutBlock.blocks[1].push(
                this.getCanvasDate(whiteBackground)
            );

            layoutBlock.blocks.forEach(column => {
                column.forEach(x => {
                    x.displayElement.isBoundToGlobalStyles = false;
                    x.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
                    x.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
                });
            });

            newBriefing.blocks.push(layoutBlock);
        }

        if (options.allowAi) {
            // heading title
            newBriefing.blocks.push(
                this.getCanvasTitle(
                    'Briefing.onlineBriefing.template.mediaCoverage.executiveSummary',
                    null,
                    BriefingHeadingLevelEnum.HEADING_2,
                    FontAlignmentEnum.left
                )
            );

            newBriefing.blocks.push(
                this.getCanvasAiAnalysis(options, options.dataSource, AiAnalysisTypes.TOP_NARRATIVES)
            );
        }

        // coverage metric charts
        let coverageMetricsLayoutBlock = this.getCanvasLayout(null, BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT);
        coverageMetricsLayoutBlock.blocks[0].push(this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_METRIC, options, ChartMetricsEnum.volumeOfMentions, 20));
        coverageMetricsLayoutBlock.blocks[1].push(this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_METRIC, options, ChartMetricsEnum.IMPRESSIONS, 20));
        newBriefing.blocks.push(coverageMetricsLayoutBlock);

        coverageMetricsLayoutBlock = this.getCanvasLayout(null, BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT);
        coverageMetricsLayoutBlock.blocks[0].push(
            this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_METRIC, options, ChartMetricsEnum.positiveSentimentPercentage, 20)
        );
        coverageMetricsLayoutBlock.blocks[1].push(
            this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_METRIC, options, ChartMetricsEnum.negativeSentimentPercentage, 20)
        );
        coverageMetricsLayoutBlock.displayElement.styles.padding = new PaddingModel(null, true, 20, 0, 0, 0);
        coverageMetricsLayoutBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        coverageMetricsLayoutBlock.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        coverageMetricsLayoutBlock.displayElement.isBoundToGlobalStyles = false;
        newBriefing.blocks.push(coverageMetricsLayoutBlock);

        // heading title
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Briefing.onlineBriefing.template.mediaCoverage.coverageHighlights',
                null,
                BriefingHeadingLevelEnum.HEADING_2,
                FontAlignmentEnum.left,
            )
        );

        if (options.allowAi) {
            const generateOnSend = options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring;
            newBriefing.blocks.push(
                this.getCanvasCurator(
                    generateOnSend,
                    AiAnalysisTypes.COLLECTION_KEY_MESSAGES_DEFAULT,
                    options.dataSource,
                    options.accentColor,
                    options.analysisFocus
                )
            );
        } else {
            if (options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring) {
                newBriefing.blocks.push(this.getCanvasAutomaticMention(options.dataSource, options.name, options.accentColor));
            } else {
                // Manual Mention Block
                newBriefing.blocks.push(this.getCanvasManualMention());
            }
        }

        // heading title
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Briefing.onlineBriefing.template.mediaCoverage.coverageInsights',
                null,
                BriefingHeadingLevelEnum.HEADING_2,
                FontAlignmentEnum.left
            )
        );

        // coverage over time chart
        let chartBlock = this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_OVER_TIME, options);
        (chartBlock.displayElement as ChartDisplayElementModel).timeInterval = ChartTimeIntervalsEnum.DYNAMIC;
        let chartTitleBlock = this.getCanvasTitle(
            this.getChartTitle(ReportBlockTypeEnum.COVERAGE_OVER_TIME, chartBlock.displayElement as ChartDisplayElementModel),
            null,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.left
        );
        newBriefing.blocks.push(chartTitleBlock);
        newBriefing.blocks.push(chartBlock);

        // coverage sentiment trend chart
        chartBlock = this.getCanvasChart(ReportBlockTypeEnum.SENTIMENT_TREND, options);
        chartTitleBlock = this.getCanvasTitle(
            this.getChartTitle(ReportBlockTypeEnum.SENTIMENT_TREND, chartBlock.displayElement as ChartDisplayElementModel),
            null,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.left
        );
        chartBlock.displayElement.styles.padding = new PaddingModel(null, true, 0, 20, 0, 20);
        chartBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        chartBlock.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        chartBlock.displayElement.isBoundToGlobalStyles = false;
        newBriefing.blocks.push(chartTitleBlock);
        newBriefing.blocks.push(chartBlock);


        if (options.allowAi) {
            // coverage sentiment breakdown chart with ai analysis
            const sentimentLayoutBlock = this.getCanvasLayout(null, BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT);
            sentimentLayoutBlock.displayElement.styles.verticalAlignment = VerticalAlignmentEnum.TOP;
            sentimentLayoutBlock.blocks[0].push(
                this.getCanvasAiAnalysis(options, options.dataSource, AiAnalysisTypes.SENTIMENT_OPINION)
            );
            chartBlock = this.getCanvasChart(ReportBlockTypeEnum.SENTIMENT_BREAKDOWN, options);
            const chartDisplayElement = (chartBlock.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel;
            chartDisplayElement.legendPosition = directionsEnum.bottom;
            chartBlock.displayElement.styles.padding = new PaddingModel(null, true, 0, 20, 20, 20);
            chartBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
            chartBlock.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
            chartBlock.displayElement.isBoundToGlobalStyles = false;
            chartTitleBlock = this.getCanvasTitle(
                this.getChartTitle(ReportBlockTypeEnum.SENTIMENT_BREAKDOWN, chartBlock.displayElement as ChartDisplayElementModel),
                null,
                BriefingHeadingLevelEnum.HEADING_3,
                FontAlignmentEnum.left
            );
            sentimentLayoutBlock.blocks[1].push(chartBlock);
            newBriefing.blocks.push(chartTitleBlock);
            newBriefing.blocks.push(sentimentLayoutBlock);
        } else {
            // coverage sentiment breakdown chart
            chartBlock = this.getCanvasChart(ReportBlockTypeEnum.SENTIMENT_BREAKDOWN, options);
            chartTitleBlock = this.getCanvasTitle(
                this.getChartTitle(ReportBlockTypeEnum.SENTIMENT_BREAKDOWN, chartBlock.displayElement as ChartDisplayElementModel),
                null,
                BriefingHeadingLevelEnum.HEADING_3,
                FontAlignmentEnum.left
            );
            newBriefing.blocks.push(chartTitleBlock);
            newBriefing.blocks.push(chartBlock);
        }

        // coverage by region chart
        chartBlock = this.getCanvasChart(ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME, options);
        chartTitleBlock = this.getCanvasTitle(
            this.getChartTitle(ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME, chartBlock.displayElement as ChartDisplayElementModel),
            null,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.left
        );
        newBriefing.blocks.push(chartTitleBlock);
        newBriefing.blocks.push(chartBlock);

        // top outlets chart
        chartBlock = this.getCanvasChart(ReportBlockTypeEnum.TOP_10_OUTLETS, options);
        chartTitleBlock = this.getCanvasTitle(
            this.getChartTitle(
                ReportBlockTypeEnum.TOP_10_OUTLETS,
                (chartBlock.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel
            ),
            null,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.left
        );
        newBriefing.blocks.push(chartTitleBlock);
        newBriefing.blocks.push(chartBlock);

        // top authors chart
        chartBlock = this.getCanvasChart(ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME, options);
        chartTitleBlock = this.getCanvasTitle(
            this.getChartTitle(
                ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME,
                (chartBlock.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel
            ),
            null,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.left
        );
        newBriefing.blocks.push(chartTitleBlock);
        newBriefing.blocks.push(chartBlock);

        return newBriefing;
    }

    public getCompetitorTemplate(options: OnlineBriefingTemplateConfigModel): BriefingModel {
        const newBriefing: BriefingModel = new BriefingModel();
        if (options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring) {
            newBriefing.sendAt.sendNow = false;
            newBriefing.sendAt.recurring = new BriefingRecurrencePatternModel();
            newBriefing.sendAt.recurring.frequencyType = DeliveryRecurrenceFrequencyEnum.daily;
            // every day of the week means daily recurrence
            newBriefing.sendAt.recurring.frequencyValues = [0, 1, 2, 3, 4, 5, 6];

            newBriefing.sendAt.recurring.times = [this.timeHelper.getTimeFromDate(
                new Date(),
                this.timezone,
                this.timeFormat
            )];
            newBriefing.sendAt.timeZone = this.timezone;
        }

        newBriefing.deliveryMethod = BriefingDeliveryMethodEnum.ONLINE;

        const whiteBackground = '#ffffff';

        let generalStyles = JSON.parse(JSON.stringify(this.briefingsService.getDefaultStyleSet(BriefingStyleStateEnum.CANVAS_1)));
        this.briefingsService.setDefaultGeneralStylesWithUserPreferences(generalStyles);
        generalStyles = this.setTemplateGeneralStyles(generalStyles, options);
        newBriefing.setGeneralStyles(generalStyles);
        this.briefingsService.setGeneralStyles(generalStyles);

        newBriefing.subject = this.localizationService.transform('Briefing.onlineBriefing.template.competitor');

        //#region --- Start of Header layout block ---
        const layoutBlock = this.getCanvasLayout(whiteBackground, BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT);
        layoutBlock.displayElement.isBoundToGlobalStyles = false;
        layoutBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        layoutBlock.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;

        // Image
        const imageFrame = options.bannerStyle === OnlineBriefingBannerStyleEnum.IMAGE_GRID ?
            BriefingImageFrameEnum.COLLAGE : BriefingImageFrameEnum.NO_FRAME ;

        const imageFrameBlock = this.getCanvasImage(
            whiteBackground,
            imageFrame,
            options.accentColor,
            options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring
        );
        imageFrameBlock.displayElement.isBoundToGlobalStyles = false;
        (imageFrameBlock.displayElement as BriefingImageDisplayElementModel).styles.padding = new PaddingModel(null, true, 20, 20, 20, 20);
        (imageFrameBlock.displayElement as BriefingImageDisplayElementModel).styles.cornerRadius = new CornerRadiusModel(12, false);

        layoutBlock.blocks[0].push(imageFrameBlock);

        // logo
        if (options.logo?.length) {
            const logoImage = this.getCanvasLogoImage(
                whiteBackground,
                BriefingImageFrameEnum.NO_FRAME,
                options.accentColor,
                false,
                options.logo
            );
            layoutBlock.blocks[1].push(logoImage);
        }

        // brand
        const brandTitle = this.getCanvasTitle(
            options.name,
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_1,
            FontAlignmentEnum.center
        );
        (brandTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel();
        (brandTitle.displayElement as BriefingTitleDisplayElementModel).styles.color = options.accentColor;
        layoutBlock.blocks[1].push(brandTitle);

        const competitorSubTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.competitor',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_1,
            FontAlignmentEnum.center
        );

        (competitorSubTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel();
        (competitorSubTitle.displayElement as BriefingTitleDisplayElementModel).styles.fontSize = '18px';

        // title
        layoutBlock.blocks[1].push(competitorSubTitle);

        // date
        layoutBlock.blocks[1].push(
            this.getCanvasDate(whiteBackground)
        );

        layoutBlock.blocks.forEach(column => {
            column.forEach(x => {
                x.displayElement.isBoundToGlobalStyles = false;
                x.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
                x.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
            });
        });

        newBriefing.blocks.push(layoutBlock);
        //#endregion
        // --- End of Header layout block ---


        //#region --- Start of Overview ---
        // Overview heading
        newBriefing.blocks.push(
            this.getCanvasTitle(
                options.name + ' - ' + this.localizationService.transform('Briefing.onlineBriefing.settings.competitor.overview'),
                null,
                BriefingHeadingLevelEnum.HEADING_1,
                FontAlignmentEnum.center,
            )
        );

        // Overview AI analysis
        if (options.allowAi) {
            newBriefing.blocks.push(
                this.getCanvasAiAnalysis(
                    options,
                    new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.allCuratedCoverageInBrief),
                    AiAnalysisTypes.EXECUTIVE_SUMMARY,
                    options.name,
                    CompletionLengthEnum.SHORT
                )
            );
        }

        // Overview Layout with cov metrics
        const coverageMetricsLayoutBlock = this.getCanvasLayout(null, BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT);
        coverageMetricsLayoutBlock.blocks[0].push(this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_METRIC, options, ChartMetricsEnum.volumeOfMentions, 20));
        coverageMetricsLayoutBlock.blocks[1].push(this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_METRIC, options, ChartMetricsEnum.IMPRESSIONS, 20));
        newBriefing.blocks.push(coverageMetricsLayoutBlock);
        //#endregion  --- Overview ---
        //  --- End of Overview ---


        //#region --- Start of Coverage Highlights ---

        // title block
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Briefing.onlineBriefing.template.mediaCoverage.coverageHighlights',
                null,
                BriefingHeadingLevelEnum.HEADING_2,
                FontAlignmentEnum.left,
            )
        );

        if (options.allowAi) {
            // curator block
            const generateOnSend = options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring;
            newBriefing.blocks.push(
                this.getCanvasCurator(
                    generateOnSend,
                    AiAnalysisTypes.COLLECTION_KEY_MESSAGES_DEFAULT,
                    options.dataSource,
                    options.accentColor,
                    options.analysisFocus ? options.name + ' - ' + options.analysisFocus : options.name,
                    // true
                )
            );
        } else {
            // mention block
            if (options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring) {
                newBriefing.blocks.push(this.getCanvasAutomaticMention(options.dataSource, options.name, options.accentColor));
            } else {
                // Manual Mention Block
                newBriefing.blocks.push(this.getCanvasManualMention(BriefingSortByEnum.POTENTIAL_AUDIENCE_MOST));
            }
        }

        //#endregion --- Coverage Highlights ---
        //  --- End of Coverage Highlights ---


        //#region --- Start of Coverage Highlights ---

        // heading
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Briefing.onlineBriefing.template.mediaCoverage.coverageInsights',
                null,
                BriefingHeadingLevelEnum.HEADING_2,
                FontAlignmentEnum.left,
            )
        );

        // summary trend title
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Briefing.onlineBriefing.template.competitor.summaryTrend',
                null,
                BriefingHeadingLevelEnum.HEADING_3,
                FontAlignmentEnum.left,
            )
        );
        // summary trend chart
        let chartBlock = this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_OVER_TIME, options, null, 20);
        newBriefing.blocks.push(chartBlock);

        // coverage by region title
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Reports.quickReport.chart.coverageRegion',
                null,
                BriefingHeadingLevelEnum.HEADING_3,
                FontAlignmentEnum.left,
            )
        );
        // coverage by region chart
        chartBlock = this.getCanvasChart(ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME, options, null, 20);
        newBriefing.blocks.push(chartBlock);

        const covHighTop10LayoutBlock = this.getCanvasLayout(null, BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT);
        chartBlock = this.getCanvasChart(ReportBlockTypeEnum.TOP_10_OUTLETS, options, null, 20);
        // eslint-disable-next-line max-len
        ((chartBlock.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel).chartHeaderSettings.entityProfile = false;
        covHighTop10LayoutBlock.blocks[0].push(
            this.getCanvasTitle(
                this.getChartTitle(
                    ReportBlockTypeEnum.TOP_10_OUTLETS,
                    (chartBlock.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel
                ),
                null,
                BriefingHeadingLevelEnum.HEADING_3,
                FontAlignmentEnum.left
            )
        );
        covHighTop10LayoutBlock.blocks[0].push(chartBlock);

        chartBlock = this.getCanvasChart(ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME, options, null, 20);
        // eslint-disable-next-line max-len
        ((chartBlock.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel).chartHeaderSettings.entityProfile = false;
        covHighTop10LayoutBlock.blocks[1].push(
            this.getCanvasTitle(
                this.getChartTitle(
                    ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME,
                    (chartBlock.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel
                ),
                null,
                BriefingHeadingLevelEnum.HEADING_3,
                FontAlignmentEnum.left
            )
        );
        covHighTop10LayoutBlock.blocks[1].push(chartBlock);

        newBriefing.blocks.push(covHighTop10LayoutBlock);


        // Top 10 Authors by Impressions title
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Briefing.onlineBriefing.template.competitor.coverageBuzz',
                null,
                BriefingHeadingLevelEnum.HEADING_3,
                FontAlignmentEnum.left,
            )
        );
        // 10 Authors by Impressions chart
        chartBlock = this.getCanvasChart(ReportBlockTypeEnum.TOP_TERMS, options, null, 20);
        // eslint-disable-next-line max-len
        ((chartBlock.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel).chartSpecificType = ChartSpecificTypesEnum.WORD_CLOUD;
        ((chartBlock.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel).series.forEach(serie => {
            serie.termType = TermTypesEnum.WORDS;
        });
        newBriefing.blocks.push(chartBlock);

        //#endregion --- Coverage Highlights ---
        //  --- End of Coverage Highlights ---


        //#region --- Start of Competitive Analysis Section ---

        // heading
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Briefing.onlineBriefing.template.competitor.competitiveAnalysis',
                null,
                BriefingHeadingLevelEnum.HEADING_1,
                FontAlignmentEnum.center                    ,
            )
        );

        // share of voice title
        newBriefing.blocks.push(
            this.getCanvasTitle(
                `Reports.chart.title.${ReportBlockTypeEnum.SHARE_OF_VOICE}`,
                null,
                BriefingHeadingLevelEnum.HEADING_2,
                FontAlignmentEnum.left,
            )
        );

        // share of voice chart
        chartBlock = this.getCanvasChart(
            ReportBlockTypeEnum.SHARE_OF_VOICE,
            options,
            ChartMetricsEnum.volumeOfMentions,
            20,
            null,
            null,
            options.competitors
        );
        newBriefing.blocks.push(chartBlock);

        // Competitive Coverage Trend title
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Briefing.onlineBriefing.template.competitor.competitiveCoverageTrend',
                null,
                BriefingHeadingLevelEnum.HEADING_2,
                FontAlignmentEnum.left,
            )
        );
        // Competitive Coverage Trend chart
        chartBlock = this.getCanvasChart(
            ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON,
            options,
            ChartMetricsEnum.volumeOfMentions,
            20,
            null,
            null,
            options.competitors
        );
        newBriefing.blocks.push(chartBlock);

        // Competitive Coverage Trend title
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Briefing.onlineBriefing.template.competitor.competitiveSentiment',
                null,
                BriefingHeadingLevelEnum.HEADING_2,
                FontAlignmentEnum.left,
            )
        );
        // Competitive Coverage Trend analysis
        if (options.allowAi) {
            newBriefing.blocks.push(
                this.getCanvasAiAnalysis(
                    options,
                    new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.allCuratedCoverageInBrief),
                    AiAnalysisTypes.SENTIMENT_OPINION,
                    options.name
                )
            );
        }
        // Competitive Coverage Trend chart
        chartBlock = this.getCanvasChart(
            ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON,
            options,
            ChartMetricsEnum.volumeOfMentions,
            null,
            null,
            null,
            options.competitors
        );
        newBriefing.blocks.push(chartBlock);


        // Competitive Coverage by Media Type title
        newBriefing.blocks.push(
            this.getCanvasTitle(
                'Briefing.onlineBriefing.template.competitor.competitiveMediaType',
                null,
                BriefingHeadingLevelEnum.HEADING_3,
                FontAlignmentEnum.left,
            )
        );
        // Competitive Coverage by Media Type chart
        chartBlock = this.getCanvasChart(
            ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON,
            options,
            ChartMetricsEnum.volumeOfMentions,
            null,
            null,
            null,
            options.competitors
        );
        newBriefing.blocks.push(chartBlock);

        //#endregion --- Competitive Analysis Section ---
        //  --- End of Competitive Analysis Section ---
        return newBriefing;
    }

    setTemplateGeneralStyles(generalStyles: any, options: OnlineBriefingTemplateConfigModel): any {
        generalStyles.mention.heading.color = options.accentColor;
        generalStyles.mention.showSectionHeading = false;
        generalStyles.mention.image = true;
        generalStyles.mention.imageSize = ImageSizeEnum.fullWidth;
        generalStyles.mention.styleState = BriefingStyleStateEnum.CUSTOM;
        generalStyles.mention.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        generalStyles.accentColor = options.accentColor;

        return generalStyles;
    }

    getCanvasTitle(title: string, color: string, headingLevel: BriefingHeadingLevelEnum, alignment: FontAlignmentEnum): BlockModel {
        const block = new BlockModel(
            null,
            briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.TITLE),
            new BriefingTitleDisplayElementModel(
                this.localizationService.transform(title),
                this.briefingsService.getDefaultStyle(BriefingBlockTypeEnum.TITLE, BriefingStyleStateEnum.CANVAS_1)
            )
        );
        block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        (block.displayElement as BriefingTitleDisplayElementModel).headingLevel = headingLevel;
        block.displayElement.styles.defaultReference = BriefingStyleStateEnum.CANVAS_1;
        if (color) {
            block.displayElement.styles.backgroundColor = color;
        }
        block.displayElement.styles.alignment = alignment;

        return block;
    }

    getCanvasDate(backgroundColor: string): BlockModel {
        const block = new BlockModel(
            null,
            briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.DATE),
            new DateDisplayElementModel(
                undefined,
                undefined,
                this.briefingsService.getDefaultStyle(BriefingBlockTypeEnum.DATE, BriefingStyleStateEnum.CANVAS_1)
            )
        );
        block.displayElement.styles.backgroundColor = backgroundColor;
        block.displayElement.styles.showViewInBrowser = false;
        block.displayElement.styles.font.alignment = FontAlignmentEnum.center;
        block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;

        return block;
    }

    private getCanvasText(
        content: string,
        backgroundColor: string,
        bold: boolean,
        padding: PaddingModel,
        alignment?: FontAlignmentEnum,
        italics: boolean = false
    ): BlockModel {
        const textElement = new BriefingTextDisplayElementModel(
            `<p style="letter-spacing: normal; margin-top: 0px; margin-bottom: 0px;">${content}</p>`,
            this.briefingsService.getDefaultStyle(BriefingBlockTypeEnum.TEXT, BriefingStyleStateEnum.CANVAS_1)
        );
        textElement.isBoundToGlobalStyles = false;
        textElement.styles.backgroundColor = backgroundColor;
        textElement.styles.bold = bold;
        textElement.styles.italics = italics;
        textElement.styles.styleState = BriefingStyleStateEnum.CANVAS_1;
        textElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        if (padding) {
            textElement.styles.padding = padding;
        }
        if (alignment) {
            textElement.styles.alignment = alignment;
        }
        const block = new BlockModel(
            undefined,
            briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.TEXT),
            textElement
        );
        block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        return block;
    }

    private getCanvasDivider(dividerColor: string, backgroundColor: string, padding: PaddingModel, height: number = 2): BlockModel {
        const dividerElement = new DividerDisplayElementModel(
            this.briefingsService.getDefaultStyle(BriefingBlockTypeEnum.DIVIDER, BriefingStyleStateEnum.CANVAS_1)
        );
        dividerElement.isBoundToGlobalStyles = false;
        dividerElement.styles.height = height;
        dividerElement.styles.dividerColor = dividerColor;
        dividerElement.styles.backgroundColor = backgroundColor;
        dividerElement.styles.styleState = BriefingStyleStateEnum.CANVAS_1;
        dividerElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        dividerElement.styles.padding = padding;
        const block = new BlockModel(
            undefined,
            briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.DIVIDER),
            dividerElement
        );
        block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        return block;
    }

    getCanvasLayout(
        backgroundColor: string,
        briefingBlockTypeEnum: BriefingBlockTypeEnum,
        layoutBlockDisplayRatiosEnum?: LayoutBlockDisplayRatiosEnum
    ): LayoutBlock {
        const blockType = briefingBlockTypeEnumToBlockTypeMap.get(briefingBlockTypeEnum) as LayoutBlockType;
        const layoutBlock = new LayoutBlock(
            null,
            blockType,
            new blockType.displayElementType() as LayoutDisplayElement);
        this.briefingsService.applyGeneralStylesToBlock(layoutBlock);

        layoutBlock.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;

        if (layoutBlockDisplayRatiosEnum) {
            (layoutBlock.displayElement as LayoutDisplayElement).styles.displayRatio = layoutBlockDisplayRatiosEnum;
        }

        if (backgroundColor) {
            layoutBlock.displayElement.styles.backgroundColor = backgroundColor;
        }
        layoutBlock.displayElement.styles.verticalAlignment = VerticalAlignmentEnum.MIDDLE;
        layoutBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        layoutBlock.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        layoutBlock.displayElement.styles.defaultReference = BriefingStyleStateEnum.BRIEFING_1;
        if (layoutBlockDisplayRatiosEnum) {
            layoutBlock.displayElement.styles.width = layoutBlockDisplayRatiosToWidthPercentMap.get(layoutBlockDisplayRatiosEnum);
        }

        return layoutBlock;
    }

    getCanvasImage(backgroundColor: string, imageFrame: BriefingImageFrameEnum, accentColor: string, automaticImage: boolean): BlockModel {
        const block = new BlockModel(
            null,
            briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.IMAGE),
            new BriefingImageDisplayElementModel(
                <BriefingImageDisplayElementApiModel>{
                    styles: this.briefingsService.getDefaultStyle(BriefingBlockTypeEnum.IMAGE, BriefingStyleStateEnum.CANVAS_1)
                }
            )
        );

        block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        (block.displayElement as BriefingImageDisplayElementModel).imageEffects.imageFrame = imageFrame;
        (block.displayElement as BriefingImageDisplayElementModel).imageEffects.primaryColor = accentColor;

        (block.displayElement as BriefingImageDisplayElementModel).images = [];
        if (automaticImage) {
            let numberOfImages = 1;
            if (imageFrame === BriefingImageFrameEnum.COLLAGE) {
                numberOfImages = 4;
            }

            for (let i = 0; i < numberOfImages; i++) {
                const imgProperty = new BriefingImagePropertiesModel();
                imgProperty.location = i;
                imgProperty.imageSource = ImageSourceEnum.AUTOMATE;
                (block.displayElement as BriefingImageDisplayElementModel).images.push(imgProperty);
            }
        }

        block.displayElement.styles.backgroundColor = backgroundColor;
        block.displayElement.styles.defaultReference = BriefingStyleStateEnum.CANVAS_1;

        return block;
    }

    getCanvasLogoImage(
        backgroundColor: string,
        imageFrame: BriefingImageFrameEnum,
        accentColor: string,
        automaticImage: boolean,
        logo: BriefingImagePropertiesModel[]
    ): BlockModel {
        const logoImage = this.getCanvasImage(
            backgroundColor,
            imageFrame,
            accentColor,
            automaticImage
        );

        (logoImage.displayElement as BriefingImageDisplayElementModel).images = logo;
        let displaySize = 1;
        const naturalWidth = logo[0]?.naturalWidth;
        const naturalHeight = logo[0]?.naturalHeight;
        if (naturalWidth && naturalHeight) {
            // The default value of `LayoutBlockComponent.blockMaxWidth` is 640px, so each column is 320px in a 2-column layout;
            const columnWidth = 640 * 0.5;
            const targetHeight = 120;
            displaySize = Math.min(1, targetHeight / naturalHeight * naturalWidth / columnWidth);
        }
        (logoImage.displayElement as BriefingImageDisplayElementModel).imageProperties.displaySize = displaySize;
        return logoImage;
    }

    getCanvasAiAnalysis(
        options: OnlineBriefingTemplateConfigModel,
        dataSource: MonitoringBackEndDataSourceModel,
        aiAnalysisType: AiAnalysisTypes,
        name?: string,
        completionLength?: CompletionLengthEnum
    ): BlockModel {
        const aiDisplayElement = new AnalysisDisplayElementModel();
        aiDisplayElement.generateOnSend = options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring;
        aiDisplayElement.analysisField = new CoverageInsights();
        if (options.analysisFocus && name) {
            aiDisplayElement.analysisField.focus = name + ' - ' + options.analysisFocus;
        } else if (options.analysisFocus) {
            aiDisplayElement.analysisField.focus = options.analysisFocus;
        } else if (name) {
            aiDisplayElement.analysisField.focus = name;
        }

        aiDisplayElement.analysisField.aiAnalysisType = aiAnalysisType;
        if (completionLength) {
            aiDisplayElement.analysisField.setLengthOptionByValue(completionLength);
        }

        aiDisplayElement.backendDataSource = dataSource;
        aiDisplayElement.filterMentionSearchModel = new MentionSearchModel();
        aiDisplayElement.filterMentionSearchModel.searchCriteria = options.dataSource.filterSearchCriteria ?? new MentionSearchCriteriaModel();

        const block = new BlockModel(
            null,
            briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.ANALYSIS),
            aiDisplayElement
        );

        block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        block.displayElement.styles.defaultReference = BriefingStyleStateEnum.CANVAS_1;
        block.displayElement.styles.styleState = BriefingStyleStateEnum.CANVAS_1;
        block.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CANVAS_1;
        block.displayElement.styles.padding = new PaddingModel(0, true, 0, 0, 20, 0);

        this.briefingsService.applyGeneralStylesToBlock(block);

        return block;
    }

    getCanvasAutomaticMention(dataSource: MonitoringBackEndDataSourceModel, name: string, accentColor: string): BlockModel {
        const dynamicBlock =
            this.briefingsService.createDynamicBlock(dataSource, this.MAX_CANVAS_BLOCK_MENTIONS, name);
        dynamicBlock.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        dynamicBlock.displayElement.styles.accentColor = accentColor;
        dynamicBlock.displayElement.styles.showSectionHeading = false;
        dynamicBlock.displayElement.styles.image = true;
        dynamicBlock.displayElement.styles.imageSize = ImageSizeEnum.fullWidth;
        dynamicBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        dynamicBlock.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        dynamicBlock.displayElement.styles.defaultReference = BriefingStyleStateEnum.BRIEFING_1;

        return dynamicBlock;
    }

    getCanvasManualMention(sortBy?: BriefingSortByEnum): BlockModel {
        const mentionSearch: MentionSearchModel = new MentionSearchModel();
        mentionSearch.type = SavedSearchType.BRIEFING;
        // need unique names for the DB
        mentionSearch.name = `${+new Date()}${Math.random()}`;
        mentionSearch.searchCriteria.query.dateQuery = null;

        const styles = this.briefingsService.getDefaultStyle(BriefingBlockTypeEnum.CURATED_MENTION_LIST, BriefingStyleStateEnum.BRIEFING_1);
        if (sortBy) {
            styles.sortBy = sortBy;
        }

        const block = new BlockModel(
            null,
            briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.CURATED_MENTION_LIST),
            new CuratedMentionListDisplayElementModel(
                this.localizationService.transform('Briefings.mentionsOfInterest'),
                [],
                [],
                null,
                mentionSearch,
                styles,
                uuid.v4(),
                this.DEFAULT_PREVIEW_MENTIONS
            )
        );
        block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        this.briefingsService.applyGeneralStylesToBlock(block);
        block.displayElement.styles.showSectionHeading = false;
        block.displayElement.styles.image = true;
        block.displayElement.styles.imageSize = ImageSizeEnum.fullWidth;
        block.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        block.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        block.displayElement.styles.defaultReference = BriefingStyleStateEnum.BRIEFING_1;

        return block;
    }

    getCanvasCurator(
        generateOnSend: boolean,
        aiAnalysisTypes: AiAnalysisTypes,
        dataSource: MonitoringBackEndDataSourceModel,
        accentColor: string,
        analysisFocus: string,
        mentionBlockOnly: boolean = false,
        isMentionBlockBoundToGlobalStyles: boolean = true
    ): BlockModel {
        const curatorDisplayElement = new CuratorDisplayElement();
        curatorDisplayElement.bulkAnalysisMessage.aiAnalysisType = aiAnalysisTypes;
        curatorDisplayElement.backendDataSource = dataSource;
        curatorDisplayElement.bulkAnalysisMessage.focus = analysisFocus;
        curatorDisplayElement.generateOnSend = generateOnSend;

        const curatorBlock = this.briefingsService.createCuratorBlock(
            briefingCuratorBlockType,
            {
                displayElement: curatorDisplayElement,
                deliveryMethod: BriefingDeliveryMethodEnum.ONLINE,
                accentColor: accentColor,
                analysisLength: CompletionLengthEnum.VERY_SHORT
            },
            BriefingStyleStateEnum.CANVAS_1
        );

        curatorBlock.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;

        if (mentionBlockOnly) {
            curatorBlock.blocks = curatorBlock.blocks.filter(x => x.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST);
        } else {
            const aiAnalysisBlock = curatorBlock.blocks.find(x => x.type.key === BriefingBlockTypeEnum.ANALYSIS);
            aiAnalysisBlock.displayElement.styles.defaultReference = BriefingStyleStateEnum.CANVAS_1;
            aiAnalysisBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CANVAS_1;
            (aiAnalysisBlock.displayElement as AnalysisDisplayElementModel).analysisField.focus = analysisFocus;
            (aiAnalysisBlock.displayElement as AnalysisDisplayElementModel).analysisField.keyTakeawayHeading = null;
        }

        const mentionBlock = curatorBlock.blocks.find(x => x.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST);
        mentionBlock.displayElement.styles.heading.color = accentColor;
        mentionBlock.displayElement.styles.defaultReference = BriefingStyleStateEnum.CANVAS_1;
        mentionBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CANVAS_1;
        mentionBlock.displayElement.isBoundToGlobalStyles = isMentionBlockBoundToGlobalStyles;

        return curatorBlock;
    }

    /**
     * Clones the data source and constrains it to rated sentiments (positive, neutral, negative),
     * excluding "not available". The sentiment % metric charts use this so their denominator matches
     * the Sentiment Breakdown chart, keeping the percentages aligned across the section.
     */
    private getRatedSentimentDataSource(dataSource: MonitoringBackEndDataSourceModel): MonitoringBackEndDataSourceModel {
        const ratedSentimentDataSource = dataSource.clone();
        if (!ratedSentimentDataSource.filterSearchCriteria) {
            ratedSentimentDataSource.filterSearchCriteria = new MentionSearchCriteriaModel();
        }
        ratedSentimentDataSource.filterSearchCriteria.query.sentiments = [
            new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive),
            new SentimentModel(SearchCriteriaQuerySentimentsEnum.neutral),
            new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)
        ];
        return ratedSentimentDataSource;
    }

    /**
     * Coverage metric chart titled with the "[metric] Coverage" variant (e.g. "Positive Coverage")
     * instead of the default "[metric] Sentiment" title shared by the rest of the app.
     */
    private getSentimentCoverageChart(options: OnlineBriefingTemplateConfigModel, metric: ChartMetricsEnum, color?: string): BlockModel {
        const chart = this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_METRIC, options, metric, 20, undefined, color);
        const chartDisplayElement = (chart.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel;
        chartDisplayElement.title = this.localizationService.transform(`Reports.chart.title.${metric}.coverage`);
        return chart;
    }

    getCanvasChart(
        reportBlockType: ReportBlockTypeEnum,
        options: OnlineBriefingTemplateConfigModel,
        metric?: ChartMetricsEnum,
        padding?: number,
        dataSource?: MonitoringBackEndDataSourceModel,
        accentColor?: string,
        competitors?: MonitoringBrand[]
    ): BlockModel {
        const block = new BlockModel(
            null,
            briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.CHART),
            new BriefingChartDisplayElementModel()
        );
        block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        this.briefingsService.applyGeneralStylesToBlock(block);
        block.displayElement.styles.accentColor = accentColor ? accentColor : options.accentColor;
        block.displayElement.styles.defaultReference = BriefingStyleStateEnum.BRIEFING_1;
        block.canAddChart = false;
        block.canEditChart = true;
        if (briefingAnnotatableBlockTypes.includes(reportBlockType)) {
            block.canAnnotate = true;
        }

        const chartBlock = new BlockModel();
        const seriesItem = this.generateReportSeriesModel(
            reportBlockType,
            dataSource ? dataSource : options.dataSource,
            accentColor ? accentColor : options.accentColor,
            metric
        );

        const chartDisplayElement = this.prepareChartDisplayElement(
            reportBlockType,
            options,
            metric,
            false,
            accentColor
        );
        chartDisplayElement.series.push(seriesItem);

        competitors?.forEach(competitor => {
            const item = this.generateReportSeriesModel(
                reportBlockType,
                competitor.dataSource,
                competitor.color,
                metric
            );

            chartDisplayElement.series.push(item);
        });

        chartBlock.displayElement = chartDisplayElement;
        chartBlock.type = reportBlockTypesMap.get(reportBlockType);
        const briefingChartDisplayElement = block.displayElement as BriefingChartDisplayElementModel;
        chartDisplayElement.series.forEach(series => {
            const mentionFilter = series.mentions?.filters;
            if (mentionFilter) {
                mentionFilter.type = SavedSearchType.BRIEFING;
                mentionFilter.searchCriteria = series.cibFilterSearchCriteriaModel();
                briefingChartDisplayElement.mentionSearches.push(mentionFilter);
            }
        });
        briefingChartDisplayElement.chartBlock = chartBlock;
        briefingChartDisplayElement.chart.generateOnSend = options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring;
        chartDisplayElement.series?.forEach(series => series.cacheResult = !briefingChartDisplayElement.chart.generateOnSend);
        const rows = reportChartInfoMap.get(reportBlockType)?.dimensions?.height;
        if (rows) {
            briefingChartDisplayElement.chart.heightPx = this.CHART_ROW_HEIGHT * rows;
        }

        if (!chartDisplayElement.styles?.colorMap) {
            chartDisplayElement.styles.colorMap = {};
        }
        chartDisplayElement.series?.forEach((series, i) => {
            chartDisplayElement.styles.colorMap[`seriesColor${i}`] = series.color;
        });

        switch (reportBlockType) {
            case ReportBlockTypeEnum.COVERAGE_METRIC:
                briefingChartDisplayElement.chart.heightPx = 200;
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                briefingChartDisplayElement.chart.heightPx = 300;
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                briefingChartDisplayElement.chart.heightPx = 375;
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                briefingChartDisplayElement.chart.heightPx = 500;
                break;
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
                briefingChartDisplayElement.chart.heightPx = 375;
                break;
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
                briefingChartDisplayElement.chart.heightPx = 500;
                break;
        }

        if (padding) {
            briefingChartDisplayElement.styles.padding = new PaddingModel(padding);
        } else {
            briefingChartDisplayElement.styles.padding = new PaddingModel();
        }

        return block;
    }

    private prepareChartDisplayElement(
        reportBlockType: ReportBlockTypeEnum,
        options: OnlineBriefingTemplateConfigModel,
        metric?: ChartMetricsEnum,
        displayTitle: boolean = false,
        accentColor?: string
    ): ChartDisplayElementModel {
        let isPreviousPeriodAllowed = true;
        if (options.dataSource.combinedSearchCriteria?.query.dateQuery.type === DateQueryTypeEnum.ALL) {
            isPreviousPeriodAllowed = false;
        }

        const chartType = reportBlockTypesMap.get(reportBlockType);
        const chartDisplayElement = new chartType.displayElementType() as ChartDisplayElementModel;
        chartDisplayElement.chartHeaderSettings.title = displayTitle;
        chartDisplayElement.chartHeaderSettings.dateRange = false;
        chartDisplayElement.chartHeaderSettings.sourceLink = false;
        chartDisplayElement.styles.chartDisplaySettings.title = displayTitle;
        chartDisplayElement.styles.chartDisplaySettings.dateRange = false;
        chartDisplayElement.styles.chartDisplaySettings.sourceLink = false;

        switch(reportBlockType) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
                chartDisplayElement.metrics = [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS];
                chartDisplayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;
                break;
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
                chartDisplayElement.metric = ChartMetricsEnum.volumeOfMentions;
                chartDisplayElement.sentiments = [
                    SearchCriteriaQuerySentimentsEnum.positive,
                    SearchCriteriaQuerySentimentsEnum.neutral,
                    SearchCriteriaQuerySentimentsEnum.negative,
                    SearchCriteriaQuerySentimentsEnum.notAvailable
                ];
                chartDisplayElement.stackedChartTypes = StackedChartTypesEnum.PERCENTAGE;
                break;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.MAP;
                chartDisplayElement.mapChartChoroplethInterpolationType = MapChartChoroplethInterpolationTypeEnum.LINEAR;
                chartDisplayElement.mapChartColorScheme = MapChartColorSchemeEnum.CHOROPLETH;
                chartDisplayElement.metric = ChartMetricsEnum.volumeOfMentions;
                chartDisplayElement.styles.chartGeographyType = 'World';
                break;

            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.PIE;
                chartDisplayElement.metric = ChartMetricsEnum.volumeOfMentions;
                chartDisplayElement.sentiments = [
                    SearchCriteriaQuerySentimentsEnum.positive,
                    SearchCriteriaQuerySentimentsEnum.neutral,
                    SearchCriteriaQuerySentimentsEnum.negative,
                    SearchCriteriaQuerySentimentsEnum.notAvailable
                ];
                chartDisplayElement.stackedChartTypes = StackedChartTypesEnum.PERCENTAGE;
                break;

            case ReportBlockTypeEnum.TOP_TERMS:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.WORD_CLOUD;
                chartDisplayElement.metric = metric ? metric : ChartMetricsEnum.VOLUME;
                chartDisplayElement.termType = TermTypesEnum.WORDS;
                chartDisplayElement.numOfItems = 10;
                break;
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.BAR;
                chartDisplayElement.chartHeaderSettings.entityProfile = true;
                chartDisplayElement.metric = ChartMetricsEnum.IMPRESSIONS;
                chartDisplayElement.labelDisplayOptions = [LabelDisplayOptionsEnum.count];
                break;
            case ReportBlockTypeEnum.COVERAGE_METRIC:
                chartDisplayElement.metric = metric;
                if (metric) {
                    (chartDisplayElement as CoverageMetricChartDisplayElement).titleIcon = metric;
                }
                (chartDisplayElement as CoverageMetricChartDisplayElement).chartSpecificType = ChartSpecificTypesEnum.LINE;
                (chartDisplayElement.chartHeaderSettings as CoverageMetricChartDisplaySettings).title = true;
                (chartDisplayElement.chartHeaderSettings as CoverageMetricChartDisplaySettings).icon = true;
                (chartDisplayElement.chartHeaderSettings as CoverageMetricChartDisplaySettings).previousPeriod = isPreviousPeriodAllowed;
                (chartDisplayElement.chartHeaderSettings as CoverageMetricChartDisplaySettings).sparkChart = true;
                chartDisplayElement.styles.accentColor = accentColor ? accentColor : options.accentColor;
                chartDisplayElement.styles.colorMap.accentColor = accentColor ? accentColor : options.accentColor;
                break;
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.COLUMN;
                chartDisplayElement.metric = metric;
                chartDisplayElement.chartHeaderSettings.previousPeriod = isPreviousPeriodAllowed;
                chartDisplayElement.styles.chartDisplaySettings.previousPeriod = isPreviousPeriodAllowed;
                chartDisplayElement.styles.colorMap.previousPeriodBarColor = '#c7c7c7';
                chartDisplayElement.styles.previousPeriodBarColorType = 'CUSTOM';
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
                chartDisplayElement.metric = metric;
                chartDisplayElement.chartHeaderSettings.multiSource = true;
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.MULTI_STACKED_COLUMN;
                chartDisplayElement.metric = metric;
                chartDisplayElement.metrics = [ChartMetricsEnum.volumeOfMentions];
                chartDisplayElement.sentiments = [
                    SearchCriteriaQuerySentimentsEnum.positive,
                    SearchCriteriaQuerySentimentsEnum.neutral,
                    SearchCriteriaQuerySentimentsEnum.negative
                ];

                break;
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.PIE;
                chartDisplayElement.metric = metric;
                chartDisplayElement.chartHeaderSettings.multiSource = true;
                chartDisplayElement.chartHeaderSettings.previousPeriod = isPreviousPeriodAllowed;
                break;
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
                chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.MULTI_STACKED_COLUMN;
                chartDisplayElement.metric = metric;
                chartDisplayElement.chartHeaderSettings.multiSource = true;
                chartDisplayElement.networkSelection = getAllMediaTypes();
                break;
        }
        chartDisplayElement.styles.chartHeaderSettings = chartDisplayElement.chartHeaderSettings;

        (chartDisplayElement as ChartDisplayElementModel).title = this.getChartTitle(reportBlockType, chartDisplayElement);

        return chartDisplayElement;
    }

    private getChartTitle(type: ReportBlockTypeEnum, displayElement: ChartDisplayElementModel): string {
        let titleStructure;
        const metric = displayElement.metric;
        switch (type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                titleStructure = this.localizationService.transform(`Reports.chart.title.${type}.structure.multiMetrics`);
                return this.reportsService.createDynamicChartTitle(titleStructure, displayElement);
            case ReportBlockTypeEnum.COVERAGE_METRIC:
                titleStructure = this.localizationService.transform(`Reports.chart.title.${type}.structure`);
                if (metric === ChartMetricsEnum.IMPRESSIONS || metric === ChartMetricsEnum.ENGAGEMENTS) {
                    titleStructure = this.localizationService.transform(`Reports.chart.title.${type}.structure.plural`);
                }
                return this.reportsService.createDynamicChartTitle(titleStructure, displayElement);
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
                titleStructure = this.localizationService.transform(`Reports.chart.title.${type}.structure`);
                return this.reportsService.createDynamicChartTitle(titleStructure, displayElement);
            default:
                return this.localizationService.transform(`Reports.chart.title.${type}`);
        }
    }

    private generateReportSeriesModel(
        reportBlockType: ReportBlockTypeEnum,
        dataSource: MonitoringBackEndDataSourceModel,
        accentColor: string,
        metric?: ChartMetricsEnum
    ): ReportSeriesModel {
        const reportSeries = new ReportSeriesModel();
        reportSeries.id = null;
        reportSeries.chartSpecificType = ChartSpecificTypesEnum.LINE;
        reportSeries.color = accentColor;
        reportSeries.dateRange = new ReportDateRangeModel();
        // The quarterly date range is owned by the source picker, which writes it to
        // filterSearchCriteria; it's only mirrored onto sourceSearchCriteria in some flows (e.g.
        // createQuarterlyReportTemplate). Fall back to filterSearchCriteria so the preview path
        // resolves a range, and guard against a null query (ReportDateRangeModel keeps its default).
        const seriesDateQuery = dataSource.sourceSearchCriteria?.query?.dateQuery
            ?? dataSource.filterSearchCriteria?.query?.dateQuery;
        if (seriesDateQuery) {
            reportSeries.dateRange.setFromDateQuery(seriesDateQuery);
        }
        reportSeries.label = dataSource.dataSourceName;
        reportSeries.chartType = reportBlockType;

        reportSeries.type = seriesSourceTypesToSeriesTypesMap.get(mentionSourceApiTypesEnumToSeriesSourceTypesEnum.get(
            monitoringSeriesTypeEnumToMentionSourceApiTypesEnum.get(dataSource.type)));

        reportSeries.mentions.filters = new MentionSearchModel();
        reportSeries.mentions.filters.searchCriteria = dataSource.filterSearchCriteria;
        reportSeries.mentions.combinedQuery = dataSource.combinedSearchCriteria.query;
        switch (reportSeries.type) {
            case SeriesTypeEnum.folder:
                reportSeries.mentions.folderId = dataSource.id;
                break;
            case SeriesTypeEnum.topic:
                reportSeries.mentions.topicId = dataSource.id;
                break;
            case SeriesTypeEnum.subscription:
                reportSeries.mentions.mediaSubscriptionId = dataSource.id;
                break;
            case SeriesTypeEnum.tag:
                reportSeries.mentions.tagId = dataSource.id;
                break;
            case SeriesTypeEnum.tagGroup:
                reportSeries.mentions.tagGroupId = dataSource.id;
                break;
            case SeriesTypeEnum.sourceGroup:
                reportSeries.mentions.sourceGroupId = dataSource.id;
                break;
            case SeriesTypeEnum.aiAgent:
                reportSeries.mentions.aiAgentId = dataSource.id;
                break;
            case SeriesTypeEnum.socialTopic:
                reportSeries.mentions.socialTopicId = dataSource.id;
                break;
            case SeriesTypeEnum.theme:
                reportSeries.mentions.themeId = dataSource.id;
                break;
            default:
                reportSeries.mentions.useCombinedQuery = true;
                break;
        }

        switch(reportBlockType) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                reportSeries.metrics = [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS];
                break;
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                reportSeries.metric = ChartMetricsEnum.volumeOfMentions;
                reportSeries.sentiments = [
                    SearchCriteriaQuerySentimentsEnum.positive,
                    SearchCriteriaQuerySentimentsEnum.neutral,
                    SearchCriteriaQuerySentimentsEnum.negative,
                    SearchCriteriaQuerySentimentsEnum.notAvailable
                ];
                break;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                reportSeries.chartSpecificType = ChartSpecificTypesEnum.MAP;
                reportSeries.metric = ChartMetricsEnum.volumeOfMentions;
                reportSeries.styles.geographyScale = 'World';
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
                reportSeries.chartSpecificType = ChartSpecificTypesEnum.PIE;
                reportSeries.metric = ChartMetricsEnum.volumeOfMentions;
                reportSeries.sentiments = [
                    SearchCriteriaQuerySentimentsEnum.positive,
                    SearchCriteriaQuerySentimentsEnum.neutral,
                    SearchCriteriaQuerySentimentsEnum.negative,
                    SearchCriteriaQuerySentimentsEnum.notAvailable
                ];
                break;
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
                reportSeries.chartSpecificType = ChartSpecificTypesEnum.BAR;
                reportSeries.metric = ChartMetricsEnum.volumeOfMentions;
                reportSeries.styles.displayEntityProfile = 'OUTLET';
                reportSeries.styles.entityProfile = true;
                reportSeries.mentions.neoFacets = [topOutletsNeoFacetRequest];
                break;
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
                reportSeries.chartSpecificType = ChartSpecificTypesEnum.BAR;
                reportSeries.metric = ChartMetricsEnum.volumeOfMentions;
                reportSeries.styles.displayEntityProfile = 'CONTACT';
                reportSeries.styles.entityProfile = true;
                reportSeries.mentions.neoFacets = [topContactsNeoFacetRequest];
                break;
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                reportSeries.metric = metric ? metric : ChartMetricsEnum.volumeOfMentions;
                reportSeries.chartSpecificType = ChartSpecificTypesEnum.COLUMN;
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                reportSeries.metric = ChartMetricsEnum.volumeOfMentions;
                reportSeries.chartSpecificType = ChartSpecificTypesEnum.LINE;
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
                reportSeries.chartSpecificType = ChartSpecificTypesEnum.MULTI_STACKED_COLUMN;
                reportSeries.metric = ChartMetricsEnum.volumeOfMentions;
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                reportSeries.metric = metric ? metric : ChartMetricsEnum.volumeOfMentions;
                reportSeries.chartSpecificType = ChartSpecificTypesEnum.PIE;
                break;
            case ReportBlockTypeEnum.TOP_TERMS:
                reportSeries.metric = metric ? metric : ChartMetricsEnum.VOLUME;
                reportSeries.chartSpecificType = ChartSpecificTypesEnum.WORD_CLOUD;
                reportSeries.termType = TermTypesEnum.WORDS;
                reportSeries.numOfItems = 10;
                break;
            case ReportBlockTypeEnum.COVERAGE_METRIC:
                reportSeries.metric = metric;
                break;
        }

        return reportSeries;
    }

    public getMonthlyDateBounds(monthlySettings: MonthlyDateRangeSettingsModel | undefined): [Date, Date] {
        const tz = this.timezone;
        const makeMoment = () => (tz ? moment().tz(tz) : moment());

        const month = monthlySettings?.selectedMonth ?? undefined;
        const year = monthlySettings?.selectedYear ?? undefined;

        if (!month || month === MonthOptionEnum.MOST_RECENT) {
            const lastMonth = makeMoment().subtract(1, 'month');
            return [
                lastMonth.clone().date(1).startOf('day').toDate(),
                lastMonth.clone().date(1).endOf('month').toDate()
            ];
        }

        // M1 → 0, M2 → 1, ... M12 → 11
        const monthIndex = parseInt(month.replace('M', ''), 10) - 1;
        const monthYear = parseInt(year ?? String(new Date().getFullYear()), 10);

        return [
            makeMoment().year(monthYear).month(monthIndex).date(1).startOf('day').toDate(),
            makeMoment().year(monthYear).month(monthIndex).date(1).endOf('month').toDate()
        ];
    }

    private resolveDateQueryToRange(dateQuery: DateQueryModel): { start: Date; end: Date } {
        // Absolute
        if (dateQuery.absoluteRange?.start && dateQuery.absoluteRange?.end) {
            return {
                start: new Date(dateQuery.absoluteRange.start),
                end: new Date(dateQuery.absoluteRange.end)
            };
        }

        if (dateQuery.absoluteDate) {
            const date = new Date(dateQuery.absoluteDate);
            return { start: date, end: date };
        }

        // Relative timezone
        if (dateQuery.relativeTimezoneDateType) {
            const start = DateQueryModel.getStartDateForRelativeTimezone(
                dateQuery.relativeTimezoneDateType,
                this.timezone
            );
            const end = DateQueryModel.getEndDateForRelativeTimezone(
                dateQuery.relativeTimezoneDateType,
                this.timezone
            );
            return { start: start.toDate(), end: end.toDate() };
        }

        // Fallback
        const [fallbackStart, fallbackEnd] = this.getMonthlyDateBounds(undefined);
        return { start: fallbackStart, end: fallbackEnd };
    }

    /**
     * Resolves the quarter selected in `options.quarterlySettings` to concrete start/end dates.
     * Public so the new-online-briefing AI flow can apply the same range to its data source
     * (the manual flow applies it through the source picker instead).
     */
    /**
     * Bounds for an explicit coverage window from the Canvas agent (inclusive YYYY-MM-DD dates),
     * resolved in the user's preference timezone like the quarterly and monthly bounds.
     * Returns null when either date is unparseable.
     */
    public getAgentCoverageDateBounds(startDate: string, endDate: string): [Date, Date] | null {
        const tz = this.timezone;
        const parse = (value: string) => (tz ? moment.tz(value, 'YYYY-MM-DD', tz) : moment(value, 'YYYY-MM-DD', true));
        const start = parse(startDate);
        const end = parse(endDate);
        if (!start.isValid() || !end.isValid()) {
            return null;
        }
        return [start.startOf('day').toDate(), end.endOf('day').toDate()];
    }

    public getQuarterlyDateBounds(options: OnlineBriefingTemplateConfigModel): [Date, Date] {
        const tz = this.timezone;
        const makeMoment = () => (tz ? moment().tz(tz) : moment());

        const quarter = options.quarterlySettings?.selectedQuarter ?? undefined;
        const year = options.quarterlySettings?.selectedYear ?? undefined;

        if (!quarter || quarter === 'MOST_RECENT') {
            const localNow = makeMoment();
            const currentMonth = localNow.month();
            const currentYear = localNow.year();
            let mostRecentStartMonth: number;
            let mostRecentStartYear = currentYear;
            if (currentMonth < 3) {
                mostRecentStartMonth = 9;
                mostRecentStartYear = currentYear - 1;
            } else if (currentMonth < 6) {
                mostRecentStartMonth = 0;
            } else if (currentMonth < 9) {
                mostRecentStartMonth = 3;
            } else {
                mostRecentStartMonth = 6;
            }
            return [
                makeMoment().year(mostRecentStartYear).month(mostRecentStartMonth).date(1).startOf('day').toDate(),
                makeMoment().year(mostRecentStartYear).month(mostRecentStartMonth + 2).date(1).endOf('month').toDate()
            ];
        }

        const quarterNum = parseInt(quarter.replace('Q', ''), 10);
        const quarterYear = parseInt(year ?? String(new Date().getFullYear()), 10);

        if (options.quarterlySettings?.isCalendarQuarters !== false) {
            const startMonth = (quarterNum - 1) * 3;
            return [
                makeMoment().year(quarterYear).month(startMonth).date(1).startOf('day').toDate(),
                makeMoment().year(quarterYear).month(startMonth + 2).date(1).endOf('month').toDate()
            ];
        }

        const fiscalStartMonths = [9, 0, 3, 6]; // FQ1: Oct, FQ2: Jan, FQ3: Apr, FQ4: Jul
        const fiscalStartMonth = fiscalStartMonths[quarterNum - 1];
        return [
            makeMoment().year(quarterYear).month(fiscalStartMonth).date(1).startOf('day').toDate(),
            makeMoment().year(quarterYear).month(fiscalStartMonth + 2).date(1).endOf('month').toDate()
        ];
    }

    // For Monthly or Quarterly
    public getGenericReportTemplate(options: OnlineBriefingTemplateConfigModel, config: BriefingTemplateConfig): BriefingModel {
        const newBriefing: BriefingModel = new BriefingModel();
        const whiteBackground = '#ffffff';
        const isQuarterlyTemplate = options.template === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_QUARTERLY;
        const generateOnSend = options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring;

        if (options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring) {
            newBriefing.sendAt.sendNow = false;
            newBriefing.sendAt.recurring = new BriefingRecurrencePatternModel();
            newBriefing.sendAt.recurring.frequencyType = config.recurrenceType;
            newBriefing.sendAt.recurring.frequencyValues = config.frequencyValues;
            newBriefing.sendAt.recurring.times = [new TimeModel(
                timeFormatsEnum.twentyFourHour,
                config.recurrenceHour as TwentyFourHourModel,
                0 as MinuteModel,
                null,
                this.timezone
            )];

            newBriefing.sendAt.timeZone = this.timezone;
            newBriefing.sendAt.allowPastContent = true;
        }

        newBriefing.deliveryMethod = BriefingDeliveryMethodEnum.ONLINE;

        let generalStyles = JSON.parse(JSON.stringify(this.briefingsService.getDefaultStyleSet(BriefingStyleStateEnum.CANVAS_1)));
        this.briefingsService.setDefaultGeneralStylesWithUserPreferences(generalStyles);
        generalStyles = this.setTemplateGeneralStyles(generalStyles, options);
        newBriefing.setGeneralStyles(generalStyles);
        this.briefingsService.setGeneralStyles(generalStyles);

        newBriefing.subject = this.localizationService.transform(config.subjectLocalization);

        //#region --- Block 1: Header layout ---
        const headerLayout = this.getCanvasLayout(whiteBackground, BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT, LayoutBlockDisplayRatiosEnum.ONE_TO_ONE);
        headerLayout.displayElement.isBoundToGlobalStyles = false;
        headerLayout.displayElement.styles.styleState = BriefingStyleStateEnum.CANVAS_1;
        headerLayout.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        headerLayout.displayElement.styles.padding = new PaddingModel(0, true, 0, 0, 0, 0);
        headerLayout.displayElement.styles.columnSpacing = 20;

        // The date range option the component is configured with drives both the date range block
        // and the overview text. A relative range (RELATIVE_TIMEZONE, e.g. "Most Recent" quarter)
        // resolves at send time, so it can't be baked into a formatted string.
        const configuredDateQuery = options.dataSource?.sourceSearchCriteria?.query?.dateQuery
            ?? options.dataSource?.filterSearchCriteria?.query?.dateQuery;
        const isRelativeDateRange = configuredDateQuery?.type === DateQueryTypeEnum.RELATIVE_TIMEZONE
            && !!configuredDateQuery.relativeTimezoneDateType;

        // Compute date bounds for display text
        let startDate: Date;
        let endDate: Date;
        let startMoment: moment.Moment;
        let endMoment: moment.Moment;
        const tz = this.timezone;
        if (isQuarterlyTemplate) {
            [startDate, endDate] = this.getQuarterlyDateBounds(options);
            startMoment = tz ? moment(startDate).tz(tz) : moment(startDate);
            endMoment = tz ? moment(endDate).tz(tz) : moment(endDate);
        } else {
            if (!options.monthlySettings?.isCalendarMonths && configuredDateQuery) {
                // Custom mode
                const resolvedRange = this.resolveDateQueryToRange(configuredDateQuery);
                startDate = resolvedRange.start;
                endDate = resolvedRange.end;
            } else {
                // Month mode
                [startDate, endDate] = this.getMonthlyDateBounds(options.monthlySettings);
            }

            startMoment = tz ? moment(startDate).tz(tz) : moment(startDate);
            endMoment = tz ? moment(endDate).tz(tz) : moment(endDate);
        }

        // For an absolute range we bake in the formatted month span; for a relative range we emit a
        // merge tag (e.g. [%LAST_MONTH%], [%LAST_QUARTER%]) so the backend resolves the period at send time.
        let overviewDateRange: string;
        if (isQuarterlyTemplate) {
            overviewDateRange = isRelativeDateRange
                ? `[%${configuredDateQuery.relativeTimezoneDateType}%]`
                : `${startMoment.format('MMMM')} - ${endMoment.format('MMMM')}, ${endMoment.format('YYYY')}`;
        } else {
            overviewDateRange = isRelativeDateRange && options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring
                ? `[%${configuredDateQuery.relativeTimezoneDateType}%]`
                : startMoment.isSame(endMoment, 'month')
                    ? `${endMoment.format('MMMM')}, ${endMoment.format('YYYY')}`
                    : `${startMoment.format('MMMM')} - ${endMoment.format('MMMM')}, ${endMoment.format('YYYY')}`;
        }

        // Column 0: image (frame based on bannerStyle)
        const imageFrame = options.bannerStyle === OnlineBriefingBannerStyleEnum.IMAGE_GRID
            ? BriefingImageFrameEnum.COLLAGE : BriefingImageFrameEnum.NO_FRAME;
        const imageBlock = this.getCanvasImage(
            whiteBackground,
            imageFrame,
            options.accentColor,
            options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring
        );
        const imageDisplayElement = imageBlock.displayElement as BriefingImageDisplayElementModel;
        imageDisplayElement.isBoundToGlobalStyles = true;
        imageDisplayElement.styles.styleState = BriefingStyleStateEnum.CANVAS_1;
        imageDisplayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        imageDisplayElement.styles.padding = new PaddingModel(0, true, 0, 0, 0, 0);
        imageDisplayElement.styles.cornerRadius = new CornerRadiusModel(20, false);
        headerLayout.blocks[0].push(imageBlock);

        // Column 1: logo (optional)
        if (options.logo?.length) {
            const logoImage = this.getCanvasLogoImage(
                whiteBackground,
                BriefingImageFrameEnum.NO_FRAME,
                options.accentColor,
                false,
                options.logo
            );
            headerLayout.blocks[1].push(logoImage);
        }

        // Column 1: H1 report title (dynamic based on quarter selection)
        let h1TitleText: string;
        if (isQuarterlyTemplate) {
            const quarter = options.quarterlySettings?.selectedQuarter ?? undefined;
            h1TitleText = this.buildReportTitle({
                periodValue: quarter,
                periodDisplay: quarter ?? '',
                year: options.quarterlySettings?.selectedYear ?? undefined,
                mediaReportSuffixKey: 'Briefing.onlineBriefing.template.quarterly.mediaReport',
                fallbackKey: 'Briefing.onlineBriefing.template.quarterly.quarterlyMediaReport'
            });
        } else {
            const month = options.monthlySettings?.selectedMonth ?? undefined;
            h1TitleText = this.buildReportTitle({
                periodValue: month,
                periodDisplay: month ? this.localizationService.transform(monthOptionLocalizationKeys[month]) : '',
                year: options.monthlySettings?.selectedYear ?? undefined,
                mediaReportSuffixKey: 'Briefing.onlineBriefing.template.quarterly.mediaReport',
                fallbackKey: 'Briefing.onlineBriefing.template.monthly.monthlyMediaReport'
            });
        }
        const h1Title = this.getCanvasTitle(h1TitleText, whiteBackground, BriefingHeadingLevelEnum.HEADING_1, FontAlignmentEnum.center);
        const h1DisplayElement = h1Title.displayElement as BriefingTitleDisplayElementModel;
        h1DisplayElement.isBoundToGlobalStyles = false;
        h1DisplayElement.styles.styleState = BriefingStyleStateEnum.CANVAS_1;
        h1DisplayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        h1DisplayElement.styles.padding = new PaddingModel(0, true, 20, 0, 0, 0);
        headerLayout.blocks[1].push(h1Title);

        // Column 1: H2 brand name
        const h2Title = this.getCanvasTitle(
            options.name || '',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_2,
            FontAlignmentEnum.center
        );
        const h2DisplayElement = h2Title.displayElement as BriefingTitleDisplayElementModel;
        h2DisplayElement.isBoundToGlobalStyles = false;
        h2DisplayElement.styles.styleState = BriefingStyleStateEnum.CANVAS_1;
        h2DisplayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        h2DisplayElement.styles.headingTwo.color = '#696969';
        h2DisplayElement.styles.headingTwo.fontFamily = 'Verdana, Geneva, sans-serif';
        h2DisplayElement.styles.headingTwo.fontSize = '22px';
        h2DisplayElement.styles.headingTwo.alignment = FontAlignmentEnum.center;
        h2DisplayElement.styles.headingTwo.padding = new PaddingModel(0, true, 10, 0, 20, 0);
        headerLayout.blocks[1].push(h2Title);

        // Column 1: "Coverage Period:" label
        headerLayout.blocks[1].push(
            this.getCanvasText(
                this.localizationService.transform('Briefing.onlineBriefing.template.quarterly.coveragePeriod'),
                whiteBackground,
                true,
                new PaddingModel(0, true, 0, 20, 0, 0)
            )
        );

        // Column 1: date range block — mirror the date range option the component is configured
        // with so the rendered range stays in sync with the briefing's actual coverage period:
        // relative (RELATIVE_TIMEZONE, e.g. "Most Recent" quarter) stays relative, absolute (a
        // specific quarter) stays absolute. Falls back to the computed absolute bounds if the
        // component has no configured query.
        const dateRangeBlock = this.getCanvasDate(whiteBackground);
        const dateRangeDisplayElement = dateRangeBlock.displayElement as DateDisplayElementModel;
        const dateRangeQuery = configuredDateQuery
            ? DateQueryModel.clone(configuredDateQuery)
            : DateQueryModel.getAbsoluteQuery();
        if (!configuredDateQuery) {
            dateRangeQuery.absoluteRange = { start: startDate, end: endDate };
        }
        if (this.timezone) {
            dateRangeQuery.timezone = this.timezone;
        }
        dateRangeDisplayElement.dateQuery = dateRangeQuery;
        dateRangeDisplayElement.styles.padding = new PaddingModel(0, true, 10, 20, 10, 0);
        dateRangeDisplayElement.styles.font.alignment = FontAlignmentEnum.left;
        headerLayout.blocks[1].push(dateRangeBlock);

        // Column 1: "Published:" label
        headerLayout.blocks[1].push(
            this.getCanvasText(
                this.localizationService.transform('Briefing.onlineBriefing.template.quarterly.published'),
                whiteBackground,
                true,
                new PaddingModel(0, true, 0, 20, 0, 0)
            )
        );

        // Column 1: date block
        const dateBlock = this.getCanvasDate(whiteBackground);
        const dateDisplayElement = dateBlock.displayElement as DateDisplayElementModel;
        dateDisplayElement.styles.padding = new PaddingModel(undefined, true, 10, 20, 10, 20);
        dateDisplayElement.styles.font.alignment = FontAlignmentEnum.left;
        headerLayout.blocks[1].push(dateBlock);

        newBriefing.blocks.push(headerLayout);
        //#endregion

        //#region --- Block 2: Divider ---
        newBriefing.blocks.push(
            this.getCanvasDivider('#696969', whiteBackground, new PaddingModel(0, true, 20, 40, 10, 40))
        );
        //#endregion

        //#region --- Block 3: Description text ---
        const overviewText = this.localizationService.transform(
            'Briefing.onlineBriefing.template.quarterly.overviewText',
            { dateRange: overviewDateRange } as LocalizationReplacementsModel
        );
        newBriefing.blocks.push(
            this.getCanvasText(overviewText, whiteBackground, false, new PaddingModel(0, true, 10, 20, 10, 0), FontAlignmentEnum.center, true)
        );
        //#endregion

        //#region --- Block 4: Divider ---
        newBriefing.blocks.push(
            this.getCanvasDivider('#696969', whiteBackground, new PaddingModel(0, true, 10, 40, 20, 40))
        );
        //#endregion

        //#region --- Block 5 & 6: Executive Summary title + AI Analysis (optional, requires AI) ---
        if (options.allowAi) {
            const execSummaryTitle = this.getCanvasTitle(
                'Briefing.onlineBriefing.template.quarterly.executiveSummary',
                whiteBackground,
                BriefingHeadingLevelEnum.HEADING_2,
                FontAlignmentEnum.center
            );
            const execSummaryDisplayElement = execSummaryTitle.displayElement as BriefingTitleDisplayElementModel;
            execSummaryDisplayElement.isBoundToGlobalStyles = false;
            execSummaryDisplayElement.styles.styleState = BriefingStyleStateEnum.CANVAS_1;
            execSummaryDisplayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
            execSummaryDisplayElement.styles.padding = new PaddingModel(0, true, 20, 20, 10, 20);
            newBriefing.blocks.push(execSummaryTitle);

            const execSummaryAnalysis = this.getCanvasAiAnalysis(
                options,
                options.dataSource,
                AiAnalysisTypes.EXECUTIVE_SUMMARY,
                options.name,
                CompletionLengthEnum.SHORT
            );
            newBriefing.blocks.push(execSummaryAnalysis);
        }

        // #endregion

        //#region --- Block 7: Coverage Metrics title ---
        const coverageMetricsTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.coverageMetrics',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.center
        );
        (coverageMetricsTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        newBriefing.blocks.push(coverageMetricsTitle);
        //#endregion

        //#region --- Block 8: Coverage Metrics layout (Volume + Impressions) ---
        const coverageMetricsLayout = this.getCanvasLayout(whiteBackground, BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT, LayoutBlockDisplayRatiosEnum.ONE_TO_ONE);
        coverageMetricsLayout.displayElement.styles.verticalAlignment = VerticalAlignmentEnum.TOP;
        coverageMetricsLayout.displayElement.styles.columnSpacing = 20;
        const volumeMetric = this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_METRIC, options, ChartMetricsEnum.volumeOfMentions, 20);
        const impressionsMetric = this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_METRIC, options, ChartMetricsEnum.IMPRESSIONS, 20);

        for (const metric of [volumeMetric, impressionsMetric]) {
            const chartBlock = (metric.displayElement as BriefingChartDisplayElementModel).chartBlock;
            const chartDisplay = chartBlock.displayElement as CoverageMetricChartDisplayElement;
            (chartDisplay.chartHeaderSettings as CoverageMetricChartDisplaySettings).sparkChart = false;
        }

        coverageMetricsLayout.blocks[0].push(volumeMetric);
        coverageMetricsLayout.blocks[1].push(impressionsMetric);
        newBriefing.blocks.push(coverageMetricsLayout);
        //#endregion

        //#region --- Block 9: Coverage Trends title ---
        const coverageTrendsTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.coverageTrends',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.center
        );
        (coverageTrendsTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        newBriefing.blocks.push(coverageTrendsTitle);
        //#endregion

        //#region --- Block 10: Coverage Over Time chart (Dynamic for monthly, weekly for quarterly) ---
        const coverageOverTimeChart = this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_OVER_TIME, options);
        const coverageOverTimeChartBlock = (coverageOverTimeChart.displayElement as BriefingChartDisplayElementModel).chartBlock;
        (coverageOverTimeChartBlock.displayElement as ChartDisplayElementModel).timeInterval = config.primaryCoverageOverTimeChartInterval;
        coverageOverTimeChart.displayElement.isBoundToGlobalStyles = false;
        newBriefing.blocks.push(coverageOverTimeChart);
        // Invisible (white) divider to create a gap between the two coverage trend charts
        newBriefing.blocks.push(
            this.getCanvasDivider(whiteBackground, whiteBackground, new PaddingModel(0, true, 0, 0, 20, 0), 1)
        );
        //#endregion

        //#region --- Block 11: Coverage Over Time chart (monthly/ quarterly, column) ---
        const secondaryCoverageOverTime = this.getCanvasChart(ReportBlockTypeEnum.COVERAGE_OVER_TIME, options);
        const secondaryCoverageOverTimeBlock = (secondaryCoverageOverTime.displayElement as BriefingChartDisplayElementModel).chartBlock;
        const secondaryCoverageOverTimeDisplayElement = secondaryCoverageOverTimeBlock.displayElement as ChartDisplayElementModel;
        secondaryCoverageOverTimeDisplayElement.timeInterval = config.secondaryCoverageOverTimeChartInterval;
        secondaryCoverageOverTimeDisplayElement.chartSpecificType = ChartSpecificTypesEnum.COLUMN;
        // For the quarterly report this is the quarter-over-quarter trend: it must span the selected
        // quarter plus the three preceding quarters, independent of the single-quarter range the rest
        // of the report uses. A relative ("Most Recent") selection stays relative so recurring reports
        // advance at send time (LAST_FOUR_QUARTERS, resolved by the backend); a specific quarter
        // resolves to an absolute four-quarter window ending at that quarter.
        if (config.secondaryCoverageOverTimeChartInterval === ChartTimeIntervalsEnum.QUARTER) {
            secondaryCoverageOverTimeDisplayElement.series?.forEach(series => {
                if (isRelativeDateRange) {
                    series.dateRange.type = DateQueryTypeEnum.RELATIVE_TIMEZONE;
                    series.dateRange.relativeTimezoneDateType = RelativeTimezoneDateTypeEnum.LAST_FOUR_QUARTERS;
                    series.dateRange.timezone = configuredDateQuery.timezone ?? this.timezone;
                    series.dateRange.absoluteRange = null;
                    series.dateRange.absoluteDate = null;
                } else {
                    series.dateRange.type = DateQueryTypeEnum.ABSOLUTE;
                    series.dateRange.absoluteRange = {
                        start: moment(startDate).subtract(9, 'months').toDate(),
                        end: endDate
                    };
                    series.dateRange.absoluteDate = null;
                    series.dateRange.relativeTimezoneDateType = null;
                }
            });
        } else if (config.secondaryCoverageOverTimeChartInterval === ChartTimeIntervalsEnum.MONTH) {
            secondaryCoverageOverTimeDisplayElement.series?.forEach(series => {
                if (isRelativeDateRange) {
                    series.dateRange.type = DateQueryTypeEnum.RELATIVE_TIMEZONE;
                    series.dateRange.relativeTimezoneDateType = RelativeTimezoneDateTypeEnum.LAST_FOUR_MONTHS;
                    series.dateRange.timezone = configuredDateQuery.timezone ?? this.timezone;
                    series.dateRange.absoluteRange = null;
                    series.dateRange.absoluteDate = null;
                } else {
                    series.dateRange.type = DateQueryTypeEnum.ABSOLUTE;
                    series.dateRange.absoluteRange = {
                        start: (tz ? moment(startDate).tz(tz) : moment(startDate)).subtract(3, 'months').toDate(),
                        end: endDate
                    };
                    series.dateRange.absoluteDate = null;
                    series.dateRange.relativeTimezoneDateType = null;
                }
            });
        }
        newBriefing.blocks.push(secondaryCoverageOverTime);
        //#endregion

        //#region --- Block 12 & 13: Coverage Highlights title + AI Analysis (optional, requires AI) ---
        if (options.allowAi) {
            const coverageHighlightsTitle = this.getCanvasTitle(
                'Briefing.onlineBriefing.template.mediaCoverage.coverageHighlights',
                whiteBackground,
                BriefingHeadingLevelEnum.HEADING_3,
                FontAlignmentEnum.center
            );
            (coverageHighlightsTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
            newBriefing.blocks.push(coverageHighlightsTitle);

            const coverageHighlightsAnalysis = this.getCanvasAiAnalysis(
                options,
                options.dataSource,
                AiAnalysisTypes.TOP_NARRATIVES,
                options.name,
                CompletionLengthEnum.MEDIUM
            );
            const coverageHighlightsAnalysisField = (coverageHighlightsAnalysis.displayElement as AnalysisDisplayElementModel).analysisField;
            coverageHighlightsAnalysisField.numOfTakeaways = 4;
            (coverageHighlightsAnalysis.displayElement as AnalysisDisplayElementModel).styles.padding = new PaddingModel(0, true, 0, 0, 20, 0);
            newBriefing.blocks.push(coverageHighlightsAnalysis);
        }
        //#endregion

        //#region --- Block 14: Sentiment Overview title ---
        const sentimentOverviewTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.sentimentOverview',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_2,
            FontAlignmentEnum.center
        );
        (sentimentOverviewTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        newBriefing.blocks.push(sentimentOverviewTitle);
        //#endregion

        //#region --- Block 15: Sentiment Breakdown chart ---
        const sentimentBreakdownChart = this.getCanvasChart(ReportBlockTypeEnum.SENTIMENT_BREAKDOWN, options);
        (sentimentBreakdownChart.displayElement as BriefingChartDisplayElementModel).chart.heightPx = 200;
        const sentimentBreakdownChartBlock = (sentimentBreakdownChart.displayElement as BriefingChartDisplayElementModel).chartBlock;
        const sentimentChartDE = sentimentBreakdownChartBlock.displayElement as ChartDisplayElementModel;
        sentimentChartDE.chartSpecificType = ChartSpecificTypesEnum.MULTI_STACKED_BAR;
        sentimentChartDE.sentiments = [
            SearchCriteriaQuerySentimentsEnum.positive,
            SearchCriteriaQuerySentimentsEnum.neutral,
            SearchCriteriaQuerySentimentsEnum.negative
        ];
        const sentimentBlockStyles = (sentimentBreakdownChart.displayElement as BriefingChartDisplayElementModel).styles;
        sentimentBlockStyles.padding = new PaddingModel(20, true, 0, 20, 0, 20);
        sentimentBlockStyles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        sentimentBreakdownChart.displayElement.isBoundToGlobalStyles = false;
        newBriefing.blocks.push(sentimentBreakdownChart);
        //#endregion

        //#region --- Block 16: Positive Coverage title ---
        const positiveCoverageTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.positiveCoverage',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.center
        );
        (positiveCoverageTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        newBriefing.blocks.push(positiveCoverageTitle);
        //#endregion

        //#region --- Block 17: Positive Coverage layout (metrics | AI analysis) ---
        const positiveCoverageLayout = this.getCanvasLayout(
            whiteBackground,
            BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT,
            options.allowAi ? LayoutBlockDisplayRatiosEnum.THREE_TO_SEVEN : LayoutBlockDisplayRatiosEnum.ONE_TO_ONE
        );
        positiveCoverageLayout.displayElement.styles.verticalAlignment = VerticalAlignmentEnum.TOP;
        positiveCoverageLayout.displayElement.styles.columnSpacing = 20;
        // Filter the sentiment % chart to rated sentiments only so its denominator (positive + neutral +
        // negative) matches the Sentiment Breakdown chart and the percentages stay in sync.
        const positiveSentimentPercentageDataSource = this.getRatedSentimentDataSource(options.dataSource);
        const positiveColor = '#0cbc7b';
        positiveCoverageLayout.blocks[0].push(
            this.getCanvasChart(
                ReportBlockTypeEnum.COVERAGE_METRIC,
                options,
                ChartMetricsEnum.positiveSentimentPercentage,
                20,
                positiveSentimentPercentageDataSource,
                positiveColor
            )
        );
        if (options.allowAi) {
            positiveCoverageLayout.blocks[0].push(
                this.getCanvasDivider('#FFFFFF', whiteBackground, new PaddingModel(0, true, 10, 0, 0, 0))
            );
            positiveCoverageLayout.blocks[0].push(
                this.getSentimentCoverageChart(options, ChartMetricsEnum.POSITIVE, positiveColor)
            );
            const positiveCoverageAnalysis = this.getCanvasAiAnalysis(
                options,
                options.dataSource,
                AiAnalysisTypes.TOP_NARRATIVES,
                this.localizationService.transform('Briefing.onlineBriefing.template.quarterly.positiveRepresentationOf'),
                CompletionLengthEnum.MEDIUM
            );
            (positiveCoverageAnalysis.displayElement as AnalysisDisplayElementModel).analysisField.numOfTakeaways = 3;
            (positiveCoverageAnalysis.displayElement as AnalysisDisplayElementModel).styles.padding = new PaddingModel(0, true, 0, 0, 20, 0);
            positiveCoverageLayout.blocks[1].push(positiveCoverageAnalysis);
        } else {
            positiveCoverageLayout.blocks[1].push(
                this.getSentimentCoverageChart(options, ChartMetricsEnum.POSITIVE, positiveColor)
            );
        }
        newBriefing.blocks.push(positiveCoverageLayout);
        //#endregion

        //#region --- Block 18: Positive Coverage curator / automatic mention / curated mention ---
        const positiveCoverageDataSource = options.dataSource.clone();
        if (!positiveCoverageDataSource.filterSearchCriteria) {
            positiveCoverageDataSource.filterSearchCriteria = new MentionSearchCriteriaModel();
        }
        positiveCoverageDataSource.filterSearchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
        if (options.allowAi) {
            // Focus the curator's analysis on the positive representation of the template's Analysis Focus,
            // falling back to the generic "Positive representation" label when no Analysis Focus was provided.
            const positiveCuratorFocus = options.analysisFocus
                ? `${this.localizationService.transform('Briefing.onlineBriefing.template.quarterly.positiveRepresentationOf')} ${options.analysisFocus}`
                : this.localizationService.transform('Briefing.onlineBriefing.template.quarterly.positiveRepresentation');
            const positiveCoverageCurator = this.getCanvasCurator(
                generateOnSend,
                AiAnalysisTypes.COLLECTION_TOP_MENTIONS,
                positiveCoverageDataSource,
                positiveColor,
                positiveCuratorFocus,
                true,
                false
            );
            newBriefing.blocks.push(positiveCoverageCurator);
        } else if (options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring) {
            const positiveCoverageAutoMention = this.getCanvasAutomaticMention(positiveCoverageDataSource, options.name, positiveColor);
            (positiveCoverageAutoMention.displayElement as DynamicMentionListDisplayElementModel).pageSize = 20;
            newBriefing.blocks.push(positiveCoverageAutoMention);
        } else {
            const positiveCoverageManualMention = this.getCanvasManualMention();
            const manualMentionDisplay = positiveCoverageManualMention.displayElement as CuratedMentionListDisplayElementModel;
            manualMentionDisplay.searchCriteria.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
            newBriefing.blocks.push(positiveCoverageManualMention);
        }
        //#endregion

        //#region --- Block 19: Negative Coverage title ---
        const negativeCoverageTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.negativeCoverage',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.center
        );
        (negativeCoverageTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        newBriefing.blocks.push(negativeCoverageTitle);
        //#endregion

        //#region --- Block 20: Negative Coverage layout (metrics | AI analysis) ---
        const negativeCoverageLayout = this.getCanvasLayout(
            whiteBackground,
            BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT,
            options.allowAi ? LayoutBlockDisplayRatiosEnum.THREE_TO_SEVEN : LayoutBlockDisplayRatiosEnum.ONE_TO_ONE
        );
        negativeCoverageLayout.displayElement.styles.verticalAlignment = VerticalAlignmentEnum.TOP;
        negativeCoverageLayout.displayElement.styles.columnSpacing = 20;
        // Filter the sentiment % chart to rated sentiments only so its denominator (positive + neutral +
        // negative) matches the Sentiment Breakdown chart and the percentages stay in sync.
        const negativeSentimentPercentageDataSource = this.getRatedSentimentDataSource(options.dataSource);
        const negativeColor = '#fd3950';
        negativeCoverageLayout.blocks[0].push(
            this.getCanvasChart(
                ReportBlockTypeEnum.COVERAGE_METRIC,
                options,
                ChartMetricsEnum.negativeSentimentPercentage,
                20,
                negativeSentimentPercentageDataSource,
                negativeColor
            )
        );
        if (options.allowAi) {
            negativeCoverageLayout.blocks[0].push(
                this.getCanvasDivider('#FFFFFF', whiteBackground, new PaddingModel(0, true, 10, 0, 0, 0))
            );
            negativeCoverageLayout.blocks[0].push(
                this.getSentimentCoverageChart(options, ChartMetricsEnum.NEGATIVE, negativeColor)
            );
            const negativeCoverageAnalysis = this.getCanvasAiAnalysis(
                options,
                options.dataSource,
                AiAnalysisTypes.TOP_NARRATIVES,
                this.localizationService.transform('Briefing.onlineBriefing.template.quarterly.negativeRepresentationOf'),
                CompletionLengthEnum.MEDIUM
            );
            (negativeCoverageAnalysis.displayElement as AnalysisDisplayElementModel).analysisField.numOfTakeaways = 3;
            (negativeCoverageAnalysis.displayElement as AnalysisDisplayElementModel).styles.padding = new PaddingModel(0, true, 0, 0, 20, 0);
            negativeCoverageLayout.blocks[1].push(negativeCoverageAnalysis);
        } else {
            negativeCoverageLayout.blocks[1].push(
                this.getSentimentCoverageChart(options, ChartMetricsEnum.NEGATIVE, negativeColor)
            );
        }
        newBriefing.blocks.push(negativeCoverageLayout);
        //#endregion

        //#region --- Block 21: Negative Coverage curator / automatic mention / curated mention ---
        const negativeCoverageDataSource = options.dataSource.clone();
        if (!negativeCoverageDataSource.filterSearchCriteria) {
            negativeCoverageDataSource.filterSearchCriteria = new MentionSearchCriteriaModel();
        }
        negativeCoverageDataSource.filterSearchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
        if (options.allowAi) {
            // Focus the curator's analysis on the negative representation of the template's Analysis Focus,
            // falling back to the generic "Negative representation" label when no Analysis Focus was provided.
            const negativeCuratorFocus = options.analysisFocus
                ? `${this.localizationService.transform('Briefing.onlineBriefing.template.quarterly.negativeRepresentationOf')} ${options.analysisFocus}`
                : this.localizationService.transform('Briefing.onlineBriefing.template.quarterly.negativeRepresentation');
            const negativeCoverageCurator = this.getCanvasCurator(
                generateOnSend,
                AiAnalysisTypes.COLLECTION_TOP_MENTIONS,
                negativeCoverageDataSource,
                negativeColor,
                negativeCuratorFocus,
                true,
                false
            );
            newBriefing.blocks.push(negativeCoverageCurator);
        } else if (options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring) {
            const negativeCoverageAutoMention = this.getCanvasAutomaticMention(negativeCoverageDataSource, options.name, options.accentColor);
            (negativeCoverageAutoMention.displayElement as DynamicMentionListDisplayElementModel).pageSize = 20;
            newBriefing.blocks.push(negativeCoverageAutoMention);
        } else {
            const negativeCoverageManualMention = this.getCanvasManualMention();
            const negativeManualMentionDisplay = negativeCoverageManualMention.displayElement as CuratedMentionListDisplayElementModel;
            negativeManualMentionDisplay.searchCriteria.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
            newBriefing.blocks.push(negativeCoverageManualMention);
        }
        //#endregion

        //#region --- Block 22: Coverage Insights title ---
        const coverageInsightsTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.coverageInsights',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_2,
            FontAlignmentEnum.center
        );
        (coverageInsightsTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        newBriefing.blocks.push(coverageInsightsTitle);
        //#endregion

        //#region --- Block 23: Coverage Insights layout (top outlets | top authors) ---
        const coverageInsightsLayout = this.getCanvasLayout(
            whiteBackground, BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT, LayoutBlockDisplayRatiosEnum.ONE_TO_ONE
        );
        coverageInsightsLayout.displayElement.styles.verticalAlignment = VerticalAlignmentEnum.TOP;
        coverageInsightsLayout.displayElement.styles.columnSpacing = 20;
        const topOutletsTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.topOutletsByImpressions',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.center
        );
        (topOutletsTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        coverageInsightsLayout.blocks[0].push(topOutletsTitle);
        const topOutletsChart = this.getCanvasChart(ReportBlockTypeEnum.TOP_10_OUTLETS, options, undefined, 20);
        const topOutletsChartDE = (topOutletsChart.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel;
        topOutletsChartDE.chartHeaderSettings.entityProfile = false;
        coverageInsightsLayout.blocks[0].push(topOutletsChart);
        const topAuthorsTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.topAuthorsByImpressions',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.center
        );
        (topAuthorsTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        coverageInsightsLayout.blocks[1].push(topAuthorsTitle);
        const topAuthorsChart = this.getCanvasChart(ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME, options, undefined, 20);
        const topAuthorsChartDE = (topAuthorsChart.displayElement as BriefingChartDisplayElementModel).chartBlock.displayElement as ChartDisplayElementModel;
        topAuthorsChartDE.chartHeaderSettings.entityProfile = false;
        coverageInsightsLayout.blocks[1].push(topAuthorsChart);
        newBriefing.blocks.push(coverageInsightsLayout);
        //#endregion

        //#region --- Block 24: Regional Coverage title ---
        const regionalCoverageTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.regionalCoverage',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.center
        );
        (regionalCoverageTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        newBriefing.blocks.push(regionalCoverageTitle);
        //#endregion

        //#region --- Block 25: Regional Coverage map chart ---
        const regionalCoverageChart = this.getCanvasChart(ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME, options, undefined, 20);
        const regionalCoverageChartBriefingDE = regionalCoverageChart.displayElement as BriefingChartDisplayElementModel;
        const regionalCoverageChartDE = regionalCoverageChartBriefingDE.chartBlock.displayElement as ChartDisplayElementModel;
        regionalCoverageChartDE.mapChartColorScheme = MapChartColorSchemeEnum.REGION_COLORS;
        regionalCoverageChartBriefingDE.chart.heightPx = this.MAP_CHART_WITH_REGION_COLORS_CHART_HEIGHT;
        newBriefing.blocks.push(regionalCoverageChart);
        //#endregion

        //#region --- Block 26: Coverage by Media Type title ---
        const coverageByMediaTypeTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.coverageByMediaType',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.center
        );
        (coverageByMediaTypeTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        newBriefing.blocks.push(coverageByMediaTypeTitle);
        //#endregion

        //#region --- Block 27: Coverage by Media Type chart ---
        const coverageByMediaTypeChart = this.getCanvasChart(ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN, options, undefined, 20);
        const coverageByMediaTypeBriefingDE = coverageByMediaTypeChart.displayElement as BriefingChartDisplayElementModel;
        // Taller chart so the pie renders larger
        coverageByMediaTypeBriefingDE.chart.heightPx = 350;
        const coverageByMediaTypeChartDE = coverageByMediaTypeBriefingDE.chartBlock.displayElement as ChartDisplayElementModel;
        coverageByMediaTypeChartDE.chartSpecificType = ChartSpecificTypesEnum.PIE;
        coverageByMediaTypeChartDE.metric = ChartMetricsEnum.volumeOfMentions;
        coverageByMediaTypeChartDE.networkSelection = getAllMediaTypes();
        newBriefing.blocks.push(coverageByMediaTypeChart);
        //#endregion

        //#region --- Block 28: Coverage Buzz title ---
        const coverageBuzzTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.coverageBuzz',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_3,
            FontAlignmentEnum.center
        );
        (coverageBuzzTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        newBriefing.blocks.push(coverageBuzzTitle);
        //#endregion

        //#region --- Block 29: Coverage Buzz word cloud chart ---
        newBriefing.blocks.push(this.getCanvasChart(ReportBlockTypeEnum.TOP_TERMS, options, undefined, 20));
        //#endregion

        //#region --- Block 30: Top Mentions title ---
        const topMentionsTitle = this.getCanvasTitle(
            'Briefing.onlineBriefing.template.quarterly.topMentions',
            whiteBackground,
            BriefingHeadingLevelEnum.HEADING_2,
            FontAlignmentEnum.center
        );
        (topMentionsTitle.displayElement as BriefingTitleDisplayElementModel).styles.padding = new PaddingModel(0, true, 20, 0, 20, 0);
        newBriefing.blocks.push(topMentionsTitle);
        //#endregion

        //#region --- Block 31: Top Mentions curator / automatic mention / curated mention ---
        if (options.allowAi) {
            // Focus the top-mentions analysis on the most impactful mentions, prefixed with the template's
            // Analysis Focus when one was provided.
            const mostImpactfulMentions = this.localizationService.transform('Briefing.onlineBriefing.template.quarterly.mostImpactfulMentions');
            const topMentionsFocus = options.analysisFocus
                ? `${options.analysisFocus}: ${mostImpactfulMentions}`
                : mostImpactfulMentions;
            const topMentionsCurator = this.getCanvasCurator(
                generateOnSend,
                AiAnalysisTypes.COLLECTION_TOP_MENTIONS,
                options.dataSource,
                options.accentColor,
                topMentionsFocus,
                true,
                false
            );
            newBriefing.blocks.push(topMentionsCurator);
        } else if (options.deliverySchedulingType === deliverySchedulingTypesEnum.recurring) {
            const topMentionsAutoMention = this.getCanvasAutomaticMention(options.dataSource, options.name, options.accentColor);
            (topMentionsAutoMention.displayElement as DynamicMentionListDisplayElementModel).pageSize = 20;
            newBriefing.blocks.push(topMentionsAutoMention);
        } else {
            newBriefing.blocks.push(this.getCanvasManualMention());
        }
        //#endregion

        return newBriefing;
    }

    private buildReportTitle(config: {
        periodValue: string | undefined;
        periodDisplay: string;
        year: string | undefined;
        mediaReportSuffixKey: string;
        fallbackKey: string;
    }): string {
        const isSpecificPeriod = config.periodValue && config.periodValue !== 'MOST_RECENT';
        if (isSpecificPeriod) {
            const suffix = this.localizationService.transform(config.mediaReportSuffixKey);
            return `${config.periodDisplay} ${config.year} ${suffix}`;
        }
        return this.localizationService.transform(config.fallbackKey);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x?.unsubscribe());
    }
}
